/* global globalThis */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "swiper/css";
import "swiper/css/navigation";
import {
  Container,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import {
  useGetLayoutVersionsByIDQuery,
  useGetPartnerZonesQuery,
} from "../Slices/roomNateApis";
import { useUserProfileSelector } from "../hooks/useUserProfile";
import { useDeleteVersionFromSavedLayoutMutation } from "../Slices/roomNateApis";
import ShoppingList from "./ShoppingList";
import { baseUrl } from "../utils/baseUrl";
import axios from "axios";
import { useGlobalAuthContext } from "../context/AuthContext";
import LayoutOptionMenu from "./LayoutOptionMenu";
import IosShareIcon from "@mui/icons-material/IosShare";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { decodeAllImages, rasterizeNode } from "../utils/captureNode";
import { ReactComponent as LogoIcon } from "../Assets/mf-logo.svg";
import { ReactComponent as PlusIcon } from "../Assets/plus.svg";
import { ReactComponent as UneyeIcon } from "../Assets/un-eye.svg";
import { ReactComponent as ShuffleIcon } from "../Assets/shuffle1.svg";
import { ReactComponent as RemoveGalleryIcon } from "../Assets/remove-menu.svg";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { ReactComponent as HideGalleryIcon } from "../Assets/eye-invisible-menu.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGlobalContext } from "../context/GlobalContext";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import LoaderSpinner from "./LoaderSpinner";
import { alpha } from "@mui/material/styles";
import { getTenantConfig } from "../utils/tenantConfig";
import BrandedExportModal from "./BrandedExport/BrandedExportModal";
import { BRANDED_EXPORT_ENABLED } from "../constants/brandedExport";
import { Helmet } from "react-helmet";
import PartnerShoppingList from "./PartnerShoppingList";
import { getMakeMagicZones } from "../utils/fallBackData";
import useUptimeTracker from "../hooks/useUptimeTracker";
import { useTrackEngagementMutation } from "../Slices/roomNateApis";
import layoutCacheStore from "../utils/Cache";
import {
  peekLastDoneEntry,
  consumeLastDoneEntry,
} from "../utils/pendingEditorBlobs";
import { setPendingShuffleIntent } from "../utils/pendingShuffleIntent";
import {
  mfLogGalleryStale,
  mfLogGuestGalleryAuth,
  summarizeImageUrl,
} from "../utils/galleryStaleTransitionDebug";
import { getBase64Image } from "../utils/covertor";
import { resolveVersionImageUrl } from "../utils/resolveVersionImage";
import { useAnalytics } from '../utils/eventLogAnalytics';
import GalleryInquireModal from "./GalleryInquireModal";
import GalleryInterestToast from "./GalleryInterestToast";
import { galleryInterestToastBottom } from "./galleryInquiryModalStyles";
import GalleryUploaderContactModal from "./GalleryUploaderContactModal";
import AuthModal from "./AuthModal";
import { useGalleryInquiry } from "../hooks/useGalleryInquiry";
import { useGalleryUploaderContact } from "../hooks/useGalleryUploaderContact";
import {
  BUYER_CONTACT_MENU_LABEL,
  canConfigureGalleryInquiries,
  dismissContactBanner,
  inquiriesAvailableForLayout,
  isGuestLayoutOwner,
  resolveGuestAuthPrefillEmail,
  shouldDisplayUploaderContactModal,
  shouldShowGuestContactBanner,
  shouldShowViewerInquire,
} from "../utils/galleryInquiryUx";
import { normalizeEmail } from "../utils/sessionAudience";
import { getAnalyticsTargetWindow } from "../utils/analyticsTargetWindow";
import Cookies from "js-cookie";
import {
  editorBottomFadeGradient,
  galleryTopFadeGradient,
  glassActionIconFilter,
  glassActionTextShadow,
} from "../theme/spatial";
import GlassActionButton from "./GlassActionButton";
import {
  GALLERY_LOGO_BUBBLE_SIZE_PX,
  GALLERY_TOP_BAR_INSET_PX,
  galleryFitSx,
} from "../constants/galleryFit";

/** Monotonic mount id for guest-auth gallery remount diagnosis. */
let layoutVersionSliderMountSeq = 0;

// eslint-disable-next-line no-unused-vars
const _console = globalThis.console; // when console is disabled, we can still use this one for specific log
// eslint-disable-next-line no-unused-vars
const console = { log() {}, warn() {}, error() {}, debug() {}, info() {}, time() {}, timeEnd() {}, group() {}, groupCollapsed() {}, groupEnd() {}, table() {} };

// Hard cap for the post-edit loader. The common clear path is
// hasUnsavedImage flipping false (server confirms image is ready).
// This cap is the safety net if that signal never fires.
const POST_EDIT_FRESH_GATE_HARD_CAP_MS = 5000;

function LayoutVersionSlider({
  showVersionLayout,
  setShowVersionLayout,
  refetchDirectoryData,
}) {
  const { currentUserSessionStorage , partner , partnerPackage} = useGlobalAuthContext();
  const { setActiveLayout, setEditVersionLayout, layoutBeingSaved, clientRef } =
    useGlobalContext();
  const { eventLog } = useAnalytics(clientRef || 'custom-ref-not-provided');

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const isGalleryUserAuthenticated = Boolean(
    currentUserSessionStorage || Cookies.get("token")
  );
  const handleGalleryLogoClick = useCallback(() => {
    if (!currentUserSessionStorage && !Cookies.get("token")) return;
    setShowVersionLayout?.(null);
    navigate("/main");
  }, [currentUserSessionStorage, setShowVersionLayout, navigate]);
  const isAdminViewerRoute =
    location.pathname.startsWith("/admin/floor-gallery/") ||
    location.pathname.startsWith("/admin/gallery/");
  /** Legacy direct mounts skip SketchUpload, so they must log webview_opened here. */
  const isDirectGalleryViewerRoute =
    location.pathname.startsWith("/gallery/") ||
    location.pathname.startsWith("/admin/gallery/");
  const {
    data: layout,
    isLoading,
    refetch,
    isError,
    isSuccess,
    isFetching,
    error,
  } = useGetLayoutVersionsByIDQuery(showVersionLayout || params?.id, {
    skip: !showVersionLayout && !params?.id,
    refetchOnMountOrArgChange: true,
  });


    const { data: partnerZones } = useGetPartnerZonesQuery(partner, {
      skip: !partner,
    });

    const zonesData = useMemo(
      () => getMakeMagicZones(partnerZones),
      [partnerZones]
    );


  const [deleteVersionFromSavedLayout] =
    useDeleteVersionFromSavedLayoutMutation();

  const [postTrackEngagement] = useTrackEngagementMutation();
  const { startTracking } = useUptimeTracker();  


  const { userProfile: userData, isUserProfileLoaded: isUserDataSuccess } = useUserProfileSelector();

  const isGalleryGuest = !currentUserSessionStorage;
  const guestLayoutOwner = isGuestLayoutOwner(layout, isGalleryGuest);
  const canConfigureInquiries = canConfigureGalleryInquiries({
    layout,
    isGuest: isGalleryGuest,
    userId: userData?._id,
  });
  const layoutInquiriesAvailable = inquiriesAvailableForLayout(layout);
  const showInquirePill = shouldShowViewerInquire({
    layout,
    isGuest: isGalleryGuest,
    userId: userData?._id,
  });

  const {
    submit: submitGalleryInquiry,
    submitting: inquireSubmitting,
    confirmState: inquireConfirmState,
    error: inquireError,
    reset: resetGalleryInquiry,
    guestEmailPrefill: inquireGuestEmailPrefill,
    canOneTap: inquireCanOneTap,
    knownEmail: inquireKnownEmail,
    submitKnown: submitKnownInquiry,
    trackIntent: trackInquiryIntent,
    trackModalOpened: trackInquiryModalOpened,
  } = useGalleryInquiry({
    layout,
    layoutUuid: layout?.uuid,
    layoutMongoId: layout?._id,
    customerRef: layout?.customer_ref || clientRef || null,
    isGuest: isGalleryGuest,
    userEmail: userData?.email,
    isInternal: Boolean(userData?.is_internal),
    inquiriesAvailable: layoutInquiriesAvailable,
  });

  const inquiryAlreadySent = Boolean(inquireConfirmState);
  const showInquireControl = showInquirePill || inquiryAlreadySent;
  const [interestToastOpen, setInterestToastOpen] = useState(false);

  /**
   * One tap when we already hold the viewer's email — the CTA is the submit, and
   * the toast carries both the confirmation and any failure, since no modal is
   * mounted on that path.
   */
  const handleInterestTap = useCallback(async () => {
    trackInquiryIntent("gallery");
    if (inquireCanOneTap) {
      const result = await submitKnownInquiry();
      setInterestToastOpen(true);
      return result;
    }
    trackInquiryModalOpened("gallery");
    setInquireModalOpen(true);
    return undefined;
  }, [
    trackInquiryIntent,
    inquireCanOneTap,
    submitKnownInquiry,
    trackInquiryModalOpened,
  ]);

  const handleCloseInterestToast = useCallback(() => {
    setInterestToastOpen(false);
    resetGalleryInquiry();
  }, [resetGalleryInquiry]);

  /** `submit` clears the previous error itself, so the toast can stay mounted. */
  const handleRetryInterest = useCallback(async () => {
    await submitKnownInquiry();
    setInterestToastOpen(true);
  }, [submitKnownInquiry]);

  const handleCloseInquireModal = useCallback(() => {
    setInquireModalOpen(false);
    resetGalleryInquiry();
  }, [resetGalleryInquiry]);

  const {
    saveContact: saveUploaderContact,
    submitting: uploaderContactSubmitting,
    saved: uploaderContactSaved,
    error: uploaderContactError,
    reset: resetUploaderContact,
  } = useGalleryUploaderContact({
    layoutUuid: layout?.uuid,
    layoutMongoId: layout?._id,
  });

  const [uploaderContactModalOpen, setUploaderContactModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authPrefillEmail, setAuthPrefillEmail] = useState("");
  const guestAuthPrefillEmailRef = useRef("");

  const handleCloseUploaderContactModal = useCallback(() => {
    if (guestLayoutOwner) {
      dismissContactBanner(layout?._id);
    }
    setUploaderContactModalOpen(false);
    resetUploaderContact();
  }, [guestLayoutOwner, layout?._id, resetUploaderContact]);

  const rememberGuestAuthPrefillEmail = useCallback((email) => {
    const prefill = normalizeEmail(email);
    if (!prefill) return;
    guestAuthPrefillEmailRef.current = prefill;
    setAuthPrefillEmail(prefill);
  }, []);

  const handleSaveUploaderContact = useCallback(
    async (email, inquiryDisplayName) => {
      const ok = await saveUploaderContact(email, inquiryDisplayName);
      if (ok) {
        rememberGuestAuthPrefillEmail(email);
        await refetch();
        refetchDirectoryData?.();
      }
      return ok;
    },
    [saveUploaderContact, refetch, refetchDirectoryData, rememberGuestAuthPrefillEmail]
  );

  useEffect(() => {
    const prefill = resolveGuestAuthPrefillEmail({
      layout: {
        _id: layout?._id,
        uuid: layout?.uuid,
        user: layout?.user,
        inquiryContactEmail: layout?.inquiryContactEmail,
      },
      cachedEmail: guestAuthPrefillEmailRef.current,
      isGuest: isGalleryGuest,
      userId: userData?._id,
    });
    if (!prefill) return;
    guestAuthPrefillEmailRef.current = prefill;
    setAuthPrefillEmail((current) => current || prefill);
  }, [
    isGalleryGuest,
    userData?._id,
    layout?._id,
    layout?.inquiryContactEmail,
    layout?.uuid,
    layout?.user,
  ]);

  const handleGalleryAuthSuccess = useCallback(() => {
    setAuthModalOpen(false);
    guestAuthPrefillEmailRef.current = "";
    setAuthPrefillEmail("");
    refetch();
    refetchDirectoryData?.();
  }, [refetch, refetchDirectoryData]);
  
  /** Gallery chips: neutral black (no brand teal on selection). */
  const galleryChipBlack = "#202124";
  const galleryChipMuted = "rgba(60, 60, 67, 0.5)";

  /**
   * Glass pills: soft omnidirectional lift (ambient blur + light drop), no backdrop-filter.
   * Active state: fill + border; avoid animating shadow.
   */
  const galleryChipGlassSx = {
    border: `1px solid ${alpha("#FFFFFF", 0.72)}`,
    background: "rgba(248, 248, 250, 0.97)",
    boxShadow: `0 0 5px ${alpha("#000000", 0.07)}, 0 2px 5px ${alpha("#000000", 0.06)}`,
  };

  const galleryChipGlassActiveSx = {
    background: "rgba(255, 255, 255, 0.99)",
    borderColor: alpha("#000000", 0.12),
    boxShadow: `0 0 6px ${alpha("#000000", 0.08)}, 0 2px 6px ${alpha("#000000", 0.07)}`,
  };

  const galleryChipGlassHoverSx = {
    background: "rgba(252, 252, 254, 0.99)",
    borderColor: alpha("#FFFFFF", 0.88),
  };

  /** Slightly brighter idle fill so “New” reads as create vs layout selection pills. */
  const galleryNewVersionChipIdleSx = {
    background: "rgba(255, 255, 255, 0.94)",
    borderColor: alpha("#FFFFFF", 0.8),
  };

  /** Side fades in the chip scroller; chips use extra padding so pills sit clear at rest. */
  const chipStripEdgeFadeWidthPx = 28;

  const galleryInquireSentCtaSx = {
    pointerEvents: "none",
    cursor: "default",
    // Terminal confirmation state ("Interest sent"): a calm white-text ghost,
    // regardless of whether the base is the solid primary or the ghost token.
    background: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "1px solid transparent",
    boxShadow: "none",
    color: "#FFFFFF",
    textShadow: glassActionTextShadow,
    "& .MuiButton-startIcon svg": { filter: glassActionIconFilter },
    "&:active": { opacity: 1 },
    "@media (hover: hover) and (pointer: fine)": {
      "&:hover": {
        backgroundColor: "transparent",
        border: "1px solid transparent",
        boxShadow: "none",
        color: "#FFFFFF",
        opacity: 1,
        textShadow: glassActionTextShadow,
        "& .MuiButton-startIcon svg": {
          filter: glassActionIconFilter,
        },
      },
      "&:focus-visible": {
        backgroundColor: "transparent",
        border: "1px solid transparent",
        boxShadow: "none",
        color: "#FFFFFF",
        opacity: 1,
        textShadow: glassActionTextShadow,
        "& .MuiButton-startIcon svg": {
          filter: glassActionIconFilter,
        },
      },
      "&:active": {
        opacity: 1,
        backgroundColor: "transparent",
        border: "1px solid transparent",
        boxShadow: "none",
        color: "#FFFFFF",
        textShadow: glassActionTextShadow,
        "& .MuiButton-startIcon svg": {
          filter: glassActionIconFilter,
        },
      },
    },
  };

  const [currentIndex, setCurrentIndex] = useState(null);
  const [paginationRange, setPaginationRange] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [optionsMenu, setOptionsMenu] = useState(null);
  const [, setMoreMenu] = useState([]);
  const [value, setValue] = useState("floor-plan");
  const [duplicatedPerformed, setDuplicatePerformed] = useState(false);
  const transformRef = useRef(null);
  const containerRef = useRef(null);
  const [brandedExportOpen, setBrandedExportOpen] = useState(false);
  const [inquireModalOpen, setInquireModalOpen] = useState(false);
  const webviewOpenedLoggedRef = useRef(null);
  const copiedTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  const versionChipsScrollerRef = useRef(null);
  const activeVersionChipColumnRef = useRef(null);
  const versionChipProgrammaticScrollRef = useRef(false);
  const [versionStripEdgeFades, setVersionStripEdgeFades] = useState({
    left: false,
    right: false,
  });
  /** True when the version chip row overflows horizontally (needs swipe). */
  const [versionStripScrollable, setVersionStripScrollable] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [viewAll, setViewAll] = useState(false);
  const [reloadRequired, setReloadRequired] = useState(
    layoutBeingSaved ? JSON.parse(JSON.stringify(layoutBeingSaved)) : null
  );
  const [creatingNewVersion, setCreatingNewVersion] = useState(false);

  // Synchronously read the blob written by runBackToGallery immediately
  // before navigate(). This module-level handoff (pendingEditorBlobs) is
  // keyed by layoutId only — no dependency on location.state — so it works
  // reliably even when React 18's concurrent renderer mounts this component
  // before the router context has committed the new location state.
  //
  // peek, never consume, during render. Done → gallery mounts this component
  // twice: runBackToGallery's GlobalContext setters commit at default priority
  // and swap the editor for the gallery while the URL is still the editor's,
  // then navigate() — wrapped in startTransition by v7_startTransition —
  // commits the route change, and /floor-plan/:layoutId/version/:version and
  // /floor-gallery/:galleryId are separate <Route> entries with their own
  // <SketchUpload />, so the whole subtree is replaced. Consuming here let the
  // throwaway first mount burn the entry, leaving the mount the user actually
  // sees with no blob — which armed postEditFreshGate and put a spinner over a
  // plan we already had the pixels for. The claim now happens in an effect
  // below, on the mount that owns the navigation.
  //
  // On a cold gallery load (no recent Done click) returns null, and behavior
  // falls through to the normal async fetchCache effect.
  const [cachedImage, setCachedImage] = useState(() => {
    const layoutId = showVersionLayout || params?.galleryId;
    if (!layoutId) return null;
    const handoff = peekLastDoneEntry(layoutId);
    mfLogGalleryStale("gallery.lazyInit", {
      layoutId,
      hadHandoff: !!handoff,
      handoffVersion: handoff?.version ?? null,
      handoffUrl: summarizeImageUrl(handoff?.url),
      editedVersionState: location?.state?.editedVersion ?? null,
    });
    return handoff || null;
  });

  // Whether this mount is the one the Done navigation produced, captured at
  // first render because the pin effect clears `editedVersion` from history as
  // soon as it honors it. The pre-navigation mount renders on the editor's own
  // URL, so it never carries the pin and never claims the handoff — that
  // asymmetry is exactly what distinguishes the throwaway mount from the real
  // one. Anything that gets this wrong fails safe: the entry is TTL'd, and the
  // durable layoutCache still holds the same blob for later mounts.
  const ownsDoneHandoffRef = useRef(
    location?.state?.editedVersion !== undefined &&
      location?.state?.editedVersion !== null
  );

  // Holds the entry set by the module-level handoff above. Outlives the
  // fetchCache closure so the effect can fall back to this blob when RTK
  // Query returns stale cached layout data synchronously (making layout
  // non-null on the first render) and currentIndex is temporarily set to the
  // wrong default before the pin effect corrects it. Without this ref,
  // fetchCache would call setCachedImage(null) for the wrong versionKey,
  // destroying the fresh blob before the pin runs.
  const freshBlobRef = useRef(cachedImage);
  const galleryMountSeqRef = useRef(null);

  // Claim the handoff, so it can't pin an edit the user has moved on from onto
  // a later gallery visit (a chip tap or back-navigation inside the TTL).
  // Deferred to an effect on the navigated mount rather than taken during the
  // lazy init above — see the peek comment for why.
  useEffect(() => {
    if (!ownsDoneHandoffRef.current) return;
    ownsDoneHandoffRef.current = false;
    const layoutId = showVersionLayout || params?.galleryId;
    if (layoutId) consumeLastDoneEntry(layoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  useEffect(() => {
    layoutVersionSliderMountSeq += 1;
    galleryMountSeqRef.current = layoutVersionSliderMountSeq;
    const layoutId = showVersionLayout || params?.galleryId;
    mfLogGuestGalleryAuth("gallery.instanceMount", {
      layoutId,
      mountSeq: layoutVersionSliderMountSeq,
      editedVersionState: location?.state?.editedVersion ?? null,
      locationStateKeys: location?.state ? Object.keys(location.state) : [],
      pathname: location?.pathname ?? null,
      locationKey: location?.key ?? null,
      hadHandoffOnInit: !!cachedImage,
      handoffVersion: cachedImage?.version ?? null,
    });
    return () => {
      mfLogGuestGalleryAuth("gallery.instanceUnmount", {
        layoutId,
        mountSeq: galleryMountSeqRef.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only
  }, []);

  useEffect(() => {
    if (!isDirectGalleryViewerRoute || !layout?._id) return;
    const galleryId = String(layout._id);
    if (webviewOpenedLoggedRef.current === galleryId) return;
    webviewOpenedLoggedRef.current = galleryId;
    eventLog(getAnalyticsTargetWindow(), "floor_plan_event", "webview_opened", {
      galleryId,
      galleryUrl: `${window.location.origin}/floor-gallery/${galleryId}`,
      origin: "link",
    });
  }, [isDirectGalleryViewerRoute, layout?._id, eventLog]);

  // Armed when we just came from the editor (editedVersion set) but the
  // synchronous init above found no fresh blob — meaning capture either
  // failed or the blob was too small to cache. While true, the render
  // shows a loader instead of the stale server image. Cleared by:
  //   (a) hasUnsavedImage flipping false — server has a fresh image ready
  //   (b) POST_EDIT_FRESH_GATE_HARD_CAP_MS timeout — absolute safety net
  const [postEditFreshGate, setPostEditFreshGate] = useState(() => {
    return !!location?.state?.editedVersion && !cachedImage;
  });

  useEffect(() => {
    if (!postEditFreshGate) return;
    const t = setTimeout(
      () => {
        mfLogGalleryStale("gallery.postEditFreshGate.clear", {
          reason: "hard_cap_ms",
          capMs: POST_EDIT_FRESH_GATE_HARD_CAP_MS,
        });
        setPostEditFreshGate(false);
      },
      POST_EDIT_FRESH_GATE_HARD_CAP_MS
    );
    return () => clearTimeout(t);
  }, [postEditFreshGate]);

  useEffect(() => {
    if (!postEditFreshGate) return;
    if (currentIndex == null) return;
    if (layout?.layoutsVersions?.[currentIndex]?.hasUnsavedImage === false) {
      mfLogGalleryStale("gallery.postEditFreshGate.clear", {
        reason: "hasUnsavedImage_false",
        currentIndex,
        version: layout?.layoutsVersions?.[currentIndex]?.version,
      });
      setPostEditFreshGate(false);
    }
  }, [postEditFreshGate, currentIndex, layout]);

  useEffect(() => {
    mfLogGalleryStale("gallery.imageLoading", { to: imageLoading, currentIndex });
  }, [imageLoading, currentIndex]);

  useEffect(() => {
    mfLogGalleryStale("gallery.postEditFreshGate", {
      active: postEditFreshGate,
      currentIndex,
    });
  }, [postEditFreshGate, currentIndex]);

  useEffect(() => {
    mfLogGalleryStale("gallery.cachedImage.state", {
      version: cachedImage?.version ?? null,
      url: summarizeImageUrl(cachedImage?.url),
      cachedAt: cachedImage?.cachedAt ?? null,
      currentIndex,
    });
  }, [cachedImage, currentIndex]);

  useEffect(() => {
    mfLogGalleryStale("gallery.rtk.flags", {
      arg: showVersionLayout || params?.id || null,
      isLoading,
      isFetching,
      isSuccess,
      isError,
    });
  }, [isLoading, isFetching, isSuccess, isError, showVersionLayout, params?.id]);

  const staleDbgLayoutSigRef = useRef("");
  useEffect(() => {
    if (currentIndex == null || !layout?.layoutsVersions?.[currentIndex]) return;
    const v = layout.layoutsVersions[currentIndex];
    const sig = `${v.version}|${v.hasUnsavedImage}|${v.furnishedImageId}|${v.quality50FurnishedImage}|${v.quality50Image}|${v.image}`;
    if (staleDbgLayoutSigRef.current === sig) return;
    staleDbgLayoutSigRef.current = sig;
    mfLogGalleryStale("gallery.api.versionSlot", {
      layoutId: layout?._id ?? null,
      currentIndex,
      version: v.version,
      hasUnsavedImage: v.hasUnsavedImage,
      furnishedImageId: summarizeImageUrl(v.furnishedImageId),
      quality50FurnishedImage: summarizeImageUrl(v.quality50FurnishedImage),
      quality50Image: summarizeImageUrl(v.quality50Image),
      image: summarizeImageUrl(v.image),
    });
  }, [layout, currentIndex]);

  useEffect(() => {
    if (currentIndex == null || !layout?.layoutsVersions?.[currentIndex]) return;
    const v = layout.layoutsVersions[currentIndex];
    const hasUnsavedImage = v?.hasUnsavedImage;
    const currentVersion = v?.version;
    const cacheVersionMismatch =
      !!cachedImage &&
      cachedImage.version !== currentVersion &&
      hasUnsavedImage;
    // Mirrors the render's expressions exactly, hasFreshBlob included —
    // without it this diagnostic reported a gate the render wasn't using.
    const hasFreshBlob = !!cachedImage;
    const shouldShowLoader =
      !hasFreshBlob &&
      (imageLoading || cacheVersionMismatch || postEditFreshGate);
    const shouldHideImage =
      !hasFreshBlob &&
      ((imageLoading && cacheVersionMismatch) || postEditFreshGate);
    mfLogGalleryStale("gallery.paintGate", {
      currentIndex,
      currentVersion,
      cachedVersion: cachedImage?.version ?? null,
      hasUnsavedImage,
      cacheVersionMismatch,
      imageLoading,
      postEditFreshGate,
      shouldShowLoader,
      shouldHideImage,
      imageWrapperDisplay: shouldShowLoader ? "none" : "flex",
    });
  }, [
    layout,
    currentIndex,
    cachedImage,
    imageLoading,
    postEditFreshGate,
  ]);

  const usePersistedState = (key, initialValue) => {
    const [state, setState] = useState(() => {
      const saved = localStorage.getItem(key);
      try {
        return saved ? JSON.parse(saved) : initialValue;
      } catch (error) {
      }
      return null;
    });

    useEffect(() => {
      localStorage.setItem(key, JSON.stringify(state));
    }, [key, state]);

    return [state, setState];
  }

  const [originalLayoutId, setOriginalLayoutId] = usePersistedState('originalLayoutId', null);

  useEffect(() => {
    const onTouchMove = (e) => {
      if (e.touches.length < 2) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchmove', onTouchMove, { passive: false });
    };
  }, []);

  useEffect(() => {
    console.log('[GALLERY-CACHE-DEBUG] useEffect fired. layout:', !!layout, 'layout._id:', layout?._id, 'currentIndex:', currentIndex);
    setOriginalLayoutId(layout?._id);

    if (!layout || currentIndex == null) {
      console.log('[GALLERY-CACHE-DEBUG] EARLY RETURN - layout:', !!layout, 'currentIndex:', currentIndex);
      return;
    }

    // While the fresh-gate is armed the lazy init above returned null because
    // capture failed. Don't pull from the cache here — there's either nothing
    // for this version or only a stale entry from a previous Done. Let the
    // gate's clear paths (hasUnsavedImage flip / 5 s cap) handle the
    // transition to the fresh server image.
    if (postEditFreshGate) {
      mfLogGalleryStale("gallery.fetchCache.skipped", {
        reason: "postEditFreshGate",
        layoutId: layout?._id ?? null,
        currentIndex,
      });
      return;
    }

    const fetchCache = async () => {
      const cacheKey = String(layout?._id);
      const cachedData = layoutCacheStore.get(cacheKey);
      const versionKey = layout?.layoutsVersions[currentIndex]?.version;
      let cachedImage = cachedData?.versions?.[versionKey];

      mfLogGalleryStale("gallery.fetchCache.start", {
        cacheKey,
        versionKey,
        storeHit: !!cachedImage?.url,
        storeUrl: summarizeImageUrl(cachedImage?.url),
        freshBlobRefVersion: freshBlobRef.current?.version ?? null,
        freshBlobRefUrl: summarizeImageUrl(freshBlobRef.current?.url),
      });
      console.log('[GALLERY-CACHE-DEBUG] cacheKey:', cacheKey);
      console.log('[GALLERY-CACHE-DEBUG] cachedData exists:', !!cachedData);
      console.log('[GALLERY-CACHE-DEBUG] cachedData versions keys:', cachedData?.versions ? Object.keys(cachedData.versions) : 'none');
      console.log('[GALLERY-CACHE-DEBUG] versionKey:', versionKey, typeof versionKey);
      console.log('[GALLERY-CACHE-DEBUG] cachedImage:', cachedImage ? 'FOUND url=' + cachedImage.url?.substring(0, 50) : 'NOT FOUND');

      // Only validate if there's a URL to check
      if (cachedImage?.url) {
        // Check if it's a blob URL - these can expire so we need to validate them
        if (cachedImage.url.startsWith('blob:')) {
          // Blob URLs created in the same session are always valid. Skip the
          // expensive GET (which downloads the full image) for entries stamped
          // within the last 60 s — this covers the editor → gallery transition
          // and eliminates the multi-second delay caused by re-fetching the blob.
          // Older blobs (e.g. restored from localStorage after a page reload)
          // still go through the validation path below.
          const isRecent = cachedImage.cachedAt && (Date.now() - cachedImage.cachedAt < 60000);
          if (isRecent) {
            freshBlobRef.current = null;
            mfLogGalleryStale("gallery.fetchCache.apply", {
              path: "blob-recent",
              versionKey,
              url: summarizeImageUrl(cachedImage.url),
            });
            setCachedImage(cachedImage);
          } else {
            try {
              // Blob URLs don't support HEAD requests (ERR_METHOD_NOT_SUPPORTED).
              // Use GET instead to validate the blob is still accessible.
              const response = await fetch(cachedImage.url, { method: "GET" });
              if (response.ok) {
                mfLogGalleryStale("gallery.fetchCache.apply", {
                  path: "blob-get-ok",
                  versionKey,
                  url: summarizeImageUrl(cachedImage.url),
                });
                setCachedImage(cachedImage);
              } else {
                mfLogGalleryStale("gallery.fetchCache.apply", {
                  path: "blob-get-fail-status",
                  status: response.status,
                  versionKey,
                });
                setCachedImage(null);
              }
            } catch {
              mfLogGalleryStale("gallery.fetchCache.apply", {
                path: "blob-get-throw",
                versionKey,
              });
              // Blob URL has expired or is invalid
              setCachedImage(null);
            }
          }
        } else {
          // For non-blob URLs, validate normally
          try {
            const isValid = await getBase64Image(cachedImage.url);
            if (isValid) {
              mfLogGalleryStale("gallery.fetchCache.apply", {
                path: "nonblob-valid",
                versionKey,
                url: summarizeImageUrl(cachedImage.url),
              });
              setCachedImage(cachedImage);
            } else {
              mfLogGalleryStale("gallery.fetchCache.apply", {
                path: "nonblob-invalid",
                versionKey,
              });
              setCachedImage(null);
            }
          } catch {
            mfLogGalleryStale("gallery.fetchCache.apply", {
              path: "nonblob-throw",
              versionKey,
            });
            setCachedImage(null);
          }
        }
      } else {
        // Nothing in layoutCacheStore for this version. Before clearing,
        // check if the module-handoff blob (freshBlobRef) covers this exact
        // version — this happens when RTK Query returns stale cached data
        // synchronously on mount, causing currentIndex to be temporarily set
        // to the wrong default before the pin effect corrects it. If the fresh
        // blob matches, restore it; if it doesn't match (user on a different
        // version chip), leave it alone — the pin effect will fire momentarily
        // and fetchCache will re-run with the correct versionKey.
        const fresh = freshBlobRef.current;
        if (fresh?.version === versionKey) {
          freshBlobRef.current = null;
          mfLogGalleryStale("gallery.fetchCache.apply", {
            path: "freshBlobRef-match",
            versionKey,
            url: summarizeImageUrl(fresh.url),
          });
          setCachedImage(fresh);
        } else if (!fresh) {
          mfLogGalleryStale("gallery.fetchCache.apply", {
            path: "clear-no-store-no-freshBlobRef",
            versionKey,
          });
          setCachedImage(null);
        } else {
          mfLogGalleryStale("gallery.fetchCache.skip", {
            path: "freshBlobRef-wrong-version",
            versionKey,
            freshVersion: fresh?.version ?? null,
          });
        }
      }

  }
    fetchCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, layout, postEditFreshGate]);

  const getRoomLabel = (zoneArray) => {
    if (!zoneArray || !Array.isArray(zoneArray)) return "Unknown Room";
    const values = zoneArray.map((zone) => zone.value);

    if (values.includes("lounging") && values.includes("dining"))
      return "Living + Dining";
    const matchedZone = zonesData.find((zone) => values.includes(zone.value));

    return matchedZone ? matchedZone.label : "Unknown Room";
  };

  const finalLayout = useMemo(() => {
    const payload = layout?.layoutsVersions?.[currentIndex]?.payload;
    const roomsData = payload?.roomsData;
    const roomActiveLayout = payload?.roomActiveLayout;
    
    if (!roomsData || !roomActiveLayout) return [];

    // =====================================================
    // 🔬 ULTRA-DEEP DIAGNOSTIC: GALLERY COORDINATE ANALYSIS
    // =====================================================
    // SECTION 0: ENTRY POINT TIMESTAMP
    // This logs EXACTLY what coordinates the API returns BEFORE any transforms.
    // Compare these values with [EDITOR] logs to find divergence point.
    // =====================================================
    if (process.env.NODE_ENV !== 'production') {
      const diagTimestamp = new Date().toISOString();
      const diagSessionId = Math.random().toString(36).substring(2, 10);
      
      console.log('');
      console.log('');
      console.log('▓'.repeat(100));
      console.log('▓'.repeat(100));
      console.log('▓▓  🖼️  [GALLERY] ULTRA-DEEP COORDINATE DIAGNOSTIC');
      console.log('▓▓  Session:', diagSessionId, '| Time:', diagTimestamp);
      console.log('▓'.repeat(100));
      console.log('▓▓  PURPOSE: Trace EXACT API data BEFORE any frontend transforms.');
      console.log('▓▓  COMPARE: Match these values with [EDITOR] logs to find divergence.');
      console.log('▓▓  KEY INSIGHT: If shuffle fixes scale, look for differences in');
      console.log('▓▓               room.scaleXCanvas BEFORE vs AFTER shuffle.');
      console.log('▓'.repeat(100));
      console.log('');
      
      // =====================================================
      // SECTION 1: PAYLOAD-LEVEL SCALE VALUES (THE CRITICAL DATA)
      // =====================================================
      console.log('┌' + '─'.repeat(98) + '┐');
      console.log('│  📊 SECTION 1: PAYLOAD SCALE VALUES - THE CRITICAL DATA                                        │');
      console.log('├' + '─'.repeat(98) + '┤');
      
      // Store for later analysis
      const sfx = payload?.scaleFactorX || 1;
      const sfy = payload?.scaleFactorY || 1;
      const scx = payload?.scaleXCanvas || 1;
      const scy = payload?.scaleYCanvas || 1;
      const apiDivWidth = payload?.divWidth || 1920;
      const apiDivHeight = payload?.divHeight || 1080;
      const currentWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
      const currentHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
      
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│  🔧 STORED SCALE VALUES (from API - what was saved):');
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│    payload.scaleXCanvas:', scx.toFixed(6), '← Room vertex viewport scale');
      console.log('│    payload.scaleYCanvas:', scy.toFixed(6));
      console.log('│    payload.scaleFactorX:', sfx.toFixed(6), '← Furniture scale factor (px per world unit)');
      console.log('│    payload.scaleFactorY:', sfy.toFixed(6));
      console.log('│  ');
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│  📐 VIEWPORT DIMENSIONS:');
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│    API SAVED VIEWPORT (when layout was created):');
      console.log('│      payload.divWidth:', apiDivWidth, 'px');
      console.log('│      payload.divHeight:', apiDivHeight, 'px');
      console.log('│    CURRENT USER VIEWPORT (right now):');
      console.log('│      window.innerWidth:', currentWidth, 'px');
      console.log('│      window.innerHeight:', currentHeight, 'px');
      console.log('│    VIEWPORT RATIO (current / saved):');
      const viewportRatioW = currentWidth / apiDivWidth;
      const viewportRatioH = currentHeight / apiDivHeight;
      console.log('│      width ratio:', viewportRatioW.toFixed(4), 'x (editor is', viewportRatioW > 1 ? 'WIDER' : 'NARROWER', ')');
      console.log('│      height ratio:', viewportRatioH.toFixed(4), 'x');
      console.log('│  ');
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│  🔑 CRITICAL RATIOS (THE ROOT OF SCALE MUTATION):');
      console.log('│  ────────────────────────────────────────────────────────────────────────────────────────────');
      console.log('│    1/scaleFactorX =', (1/sfx).toFixed(4), '← Editor MULTIPLIES furniture by this!');
      console.log('│    scaleXCanvas/scaleFactorX =', (scx/sfx).toFixed(4));
      console.log('│    scaleFactorX/scaleXCanvas =', (sfx/scx).toFixed(4));
      console.log('│  ');
      console.log('│  ⚠️  EXPECTED CAMERA RATIO (when editor loads):');
      const expectedCameraRatio = currentWidth / apiDivWidth * (scx > 0 ? 1 : 0.848);
      console.log('│    Approximate:', expectedCameraRatio.toFixed(4), '(viewport change scaling)');
      console.log('│  ');
      console.log('│  🚨 COMBINED FURNITURE SCALE FACTOR:');
      const combinedFurnitureScale = (1/sfx) * expectedCameraRatio;
      console.log('│    (1/scaleFactorX) × cameraRatio ≈', combinedFurnitureScale.toFixed(4), 'x');
      if (combinedFurnitureScale > 1.5) {
        console.log('│    ⚠️⚠️⚠️ FURNITURE WILL BE', combinedFurnitureScale.toFixed(2) + 'x LARGER THAN STORED! ⚠️⚠️⚠️');
      }
      console.log('└' + '─'.repeat(98) + '┘');
      console.log('');
      
      // =====================================================
      // SECTION 2: PER-ROOM COORDINATE ULTRA-DEEP DIVE
      // =====================================================
      console.log('┌' + '─'.repeat(98) + '┐');
      console.log('│  📊 SECTION 2: PER-ROOM COORDINATE ANALYSIS (ULTRA-DETAILED)                                   │');
      console.log('├' + '─'.repeat(98) + '┤');
      
      // Collect summary data for section 3
      const roomSummaries = [];
      
      roomsData?.forEach((room, i) => {
        const roomSfx = room?.scaleX || sfx;
        const roomScx = room?.scaleXCanvas || scx;
        const roomCameraRatio = scx > 0 ? (scx / roomScx) : 1;
        
        console.log('│');
        console.log('│  ╔═══════════════════════════════════════════════════════════════════════════════════════════╗');
        console.log('│  ║  ROOM ' + i + ': ' + (room?.roomId || 'unknown') + '                                                                    ');
        console.log('│  ╚═══════════════════════════════════════════════════════════════════════════════════════════╝');
        console.log('│');
        console.log('│  ┌─ Room-Level Scale Values (STORED with this room) ────────────────────────────────────────┐');
        console.log('│  │  room.scaleXCanvas:', (room?.scaleXCanvas || 'N/A'), '← Viewport scale when room was created');
        console.log('│  │  room.scaleYCanvas:', (room?.scaleYCanvas || 'N/A'));
        console.log('│  │  room.scaleX:', (room?.scaleX || 'N/A'), '← scaleFactorX for this room');
        console.log('│  │  room.scaleY:', (room?.scaleY || 'N/A'));
        console.log('│  │  room.offsetX:', (room?.offsetX ?? 'N/A'));
        console.log('│  │  room.offsetY:', (room?.offsetY ?? 'N/A'));
        console.log('│  │  room.centerX:', (room?.centerX || 'N/A'));
        console.log('│  │  room.centerY:', (room?.centerY || 'N/A'));
        console.log('│  └────────────────────────────────────────────────────────────────────────────────────────────┘');
        console.log('│');
        
        // Calculate what the editor will compute for camera ratio
        console.log('│  ┌─ CAMERA RATIO CALCULATION (Editor will use this) ────────────────────────────────────────┐');
        console.log('│  │  Formula: cameraRatio = current.scaleXCanvas / room.scaleXCanvas');
        console.log('│  │  cameraRatio = ' + scx.toFixed(4) + ' / ' + (roomScx || 'N/A'));
        if (roomScx) {
          console.log('│  │  cameraRatio = ' + roomCameraRatio.toFixed(6));
          if (Math.abs(roomCameraRatio - 1) > 0.01) {
            console.log('│  │  ⚠️  cameraRatio ≠ 1 → Coordinates WILL be scaled by ' + roomCameraRatio.toFixed(4) + 'x');
          } else {
            console.log('│  │  ✅ cameraRatio ≈ 1 → Coordinates will NOT change significantly');
          }
        }
        console.log('│  └────────────────────────────────────────────────────────────────────────────────────────────┘');
        console.log('│');
        
        // Get first vertex to show coordinate space
        const firstVertex = room?.payloadVertices?.[0]?.points?.[0] || room?.orignalPayloadVertices?.[0]?.points?.[0];
        if (firstVertex) {
          console.log('│  ┌─ Room Vertices (First Point) ──────────────────────────────────────────────────────────────┐');
          console.log('│  │  stored vertex: x=' + firstVertex.x?.toFixed(2) + ', y=' + firstVertex.y?.toFixed(2));
          console.log('│  │  coordinate space: SCREEN (already scaled for display)');
          console.log('│  │  canonical (÷ scaleXCanvas): x=' + (firstVertex.x / (roomScx || 1)).toFixed(2) + ', y=' + (firstVertex.y / (roomScx || 1)).toFixed(2));
          console.log('│  └────────────────────────────────────────────────────────────────────────────────────────────┘');
          console.log('│');
        }
        
        // Furniture analysis - EXTREMELY DETAILED
        const layoutIndex = roomActiveLayout?.[i];
        const furniture = room?.layouts?.[layoutIndex]?.furniture;
        
        if (furniture && furniture.length > 0) {
          console.log('│  ┌─ FURNITURE COORDINATE ANALYSIS (first 2 items) ────────────────────────────────────────┐');
          furniture.slice(0, 2).forEach((furn, fIdx) => {
            console.log('│  │');
            console.log('│  │  ════════════════════════════════════════════════════════════════════════════════════════');
            console.log('│  │  📦 ITEM [' + fIdx + ']: ' + (furn.name || 'unnamed'));
            console.log('│  │  ════════════════════════════════════════════════════════════════════════════════════════');
            console.log('│  │');
            
            // ALL stored position types
            const posX = furn.position?.x;
            const posY = furn.position?.y;
            const exactX = furn.exactPosition?.x;
            const exactY = furn.exactPosition?.y;
            const origX = furn.origionalPosition?.x;
            const origY = furn.origionalPosition?.y;
            const rawX = exactX ?? origX ?? 0;
            const rawY = exactY ?? origY ?? 0;
            
            console.log('│  │  STORED POSITIONS (from API):');
            console.log('│  │    position:         x=' + (posX?.toFixed(4) ?? 'N/A') + ', y=' + (posY?.toFixed(4) ?? 'N/A') + ' (screen coords)');
            console.log('│  │    exactPosition:    x=' + (exactX?.toFixed(4) ?? 'N/A') + ', y=' + (exactY?.toFixed(4) ?? 'N/A') + ' (canonical/world)');
            console.log('│  │    origionalPosition: x=' + (origX?.toFixed(4) ?? 'N/A') + ', y=' + (origY?.toFixed(4) ?? 'N/A'));
            console.log('│  │    → rawX (for calc): ' + rawX.toFixed(4) + ', rawY: ' + rawY.toFixed(4) + ' (exactXY ?? origXY)');
            console.log('│  │');
            
            // SIZE data
            console.log('│  │  STORED SIZES:');
            console.log('│  │    width:          ' + (furn.width?.toFixed(4) ?? 'N/A') + ' (screen px)');
            console.log('│  │    height:         ' + (furn.height?.toFixed(4) ?? 'N/A') + ' (screen px)');
            console.log('│  │    origionalWidth:  ' + (furn.origionalWidth ?? 'N/A') + ' (canonical units)');
            console.log('│  │    origionalHeight: ' + (furn.origionalHeight ?? 'N/A') + ' (canonical units)');
            console.log('│  │');
            
            // COORDINATE SPACE DETECTION
            console.log('│  │  🔬 COORDINATE SPACE DETECTION:');
            if (posX !== undefined && exactX !== undefined && exactX !== 0) {
              const posExactRatio = posX / exactX;
              console.log('│  │    position.x / exactPosition.x = ' + posX.toFixed(4) + ' / ' + exactX.toFixed(4) + ' = ' + posExactRatio.toFixed(4));
              console.log('│  │    1/scaleFactorX (expected ratio) = ' + (1/roomSfx).toFixed(4));
              if (Math.abs(posExactRatio - (1/roomSfx)) < 0.1) {
                console.log('│  │    ✅ Ratios MATCH → position is screen, exactPosition is canonical');
              } else if (Math.abs(posExactRatio - 1) < 0.01) {
                console.log('│  │    ⚠️ position ≈ exactPosition → SAME coordinate space (unusual!)');
              } else {
                console.log('│  │    ❓ Ratios DON\'T match expected → coordinate space unclear');
              }
            }
            console.log('│  │');
            
            // EDITOR PREDICTION - THE KEY CALCULATION
            console.log('│  │  🔮 EDITOR TRANSFORM PREDICTION:');
            console.log('│  │    ─────────────────────────────────────────────────────────────────────────────────────');
            console.log('│  │    Formula: correctedX = (rawX / scaleFactorX) × renderScaleCorrection × cameraRatio');
            console.log('│  │    ─────────────────────────────────────────────────────────────────────────────────────');
            
            const denomX = roomSfx; // Editor uses room.scaleX as denominator
            const step1 = rawX / denomX;
            const step2 = step1 * 1.0; // renderScaleCorrection usually 1
            const predictedX = step2 * roomCameraRatio;
            
            console.log('│  │    Step 1: rawX / scaleFactorX = ' + rawX.toFixed(4) + ' / ' + denomX.toFixed(4) + ' = ' + step1.toFixed(4));
            console.log('│  │    Step 2: × renderScaleCorrection (1.0) = ' + step2.toFixed(4));
            console.log('│  │    Step 3: × cameraRatio (' + roomCameraRatio.toFixed(4) + ') = ' + predictedX.toFixed(4));
            console.log('│  │    ─────────────────────────────────────────────────────────────────────────────────────');
            console.log('│  │    📊 PREDICTED position.x in editor: ' + predictedX.toFixed(2) + ' px');
            console.log('│  │');
            
            // WIDTH PREDICTION
            const predWidth = ((furn.origionalWidth || 0) / denomX) * 1.0 * roomCameraRatio;
            console.log('│  │    📊 PREDICTED width in editor: ' + predWidth.toFixed(2) + ' px');
            console.log('│  │');
            
            // GALLERY vs EDITOR COMPARISON
            console.log('│  │  📏 GALLERY vs EDITOR COMPARISON:');
            console.log('│  │    ─────────────────────────────────────────────────────────────────────────────────────');
            if (posX !== undefined) {
              const deltaX = predictedX - posX;
              const ratioX = posX !== 0 ? predictedX / posX : 0;
              console.log('│  │    POSITION X:');
              console.log('│  │      Gallery (stored): ' + posX.toFixed(2) + ' px');
              console.log('│  │      Editor (predicted): ' + predictedX.toFixed(2) + ' px');
              console.log('│  │      Delta: ' + deltaX.toFixed(2) + ' px');
              console.log('│  │      Ratio: ' + ratioX.toFixed(4) + 'x');
              if (Math.abs(deltaX) > 50) {
                console.log('│  │      🚨🚨🚨 LARGE DELTA > 50px! FURNITURE WILL JUMP! 🚨🚨🚨');
              } else if (Math.abs(deltaX) > 10) {
                console.log('│  │      ⚠️ Noticeable delta > 10px');
              } else {
                console.log('│  │      ✅ Delta < 10px (acceptable)');
              }
            }
            console.log('│  │');
            
            if (furn.width !== undefined) {
              const deltaW = predWidth - furn.width;
              const ratioW = furn.width !== 0 ? predWidth / furn.width : 0;
              console.log('│  │    WIDTH:');
              console.log('│  │      Gallery (stored): ' + furn.width.toFixed(2) + ' px');
              console.log('│  │      Editor (predicted): ' + predWidth.toFixed(2) + ' px');
              console.log('│  │      Delta: ' + deltaW.toFixed(2) + ' px');
              console.log('│  │      Ratio: ' + ratioW.toFixed(4) + 'x (this is the "blow up" factor)');
              if (Math.abs(ratioW - 1) > 0.2) {
                console.log('│  │      🚨🚨🚨 SIZE MUTATION! Furniture will be ' + ratioW.toFixed(2) + 'x size! 🚨🚨🚨');
              }
            }
            
            // Store for summary
            roomSummaries.push({
              room: room?.roomId,
              item: furn.name,
              storedX: posX,
              predictedX: predictedX,
              deltaX: posX !== undefined ? predictedX - posX : null,
              storedW: furn.width,
              predictedW: predWidth,
              cameraRatio: roomCameraRatio,
              blowUpFactor: 1/denomX,
            });
          });
          
          if (furniture.length > 2) {
            console.log('│  │  ... and ' + (furniture.length - 2) + ' more items');
          }
          console.log('│  └────────────────────────────────────────────────────────────────────────────────────────────┘');
        } else {
          console.log('│  │  (No furniture in this room)');
        }
      });
      
      console.log('└' + '─'.repeat(98) + '┘');
      console.log('');
      
      // =====================================================
      // SECTION 3: ROOT CAUSE ANALYSIS (DETAILED)
      // =====================================================
      console.log('┌' + '─'.repeat(98) + '┐');
      console.log('│  🧠 SECTION 3: ROOT CAUSE ANALYSIS                                                              │');
      console.log('├' + '─'.repeat(98) + '┤');
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  THE TWO TRANSFORM FORMULAS (THE MISMATCH):');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│  1️⃣  ROOM VERTICES transform:');
      console.log('│     Formula: screenPos = (stored - offset) / storedScale × currentScale + currentOffset');
      console.log('│     Simplified: screenPos ≈ stored × (currentScale / storedScale)');
      console.log('│     Simplified: screenPos ≈ stored × cameraRatio');
      console.log('│     Effect: Scales room outline by viewport ratio only');
      console.log('│');
      console.log('│  2️⃣  FURNITURE transform:');
      console.log('│     Formula: screenPos = (exactPosition / scaleFactorX) × renderScaleCorrection × cameraRatio');
      console.log('│     With values: screenPos = (exactPosition / ' + sfx.toFixed(4) + ') × 1.0 × cameraRatio');
      console.log('│     Effect: EXTRA ÷ scaleFactorX = EXTRA × ' + (1/sfx).toFixed(4));
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  🚨 THE BUG: FURNITURE GETS AN EXTRA MULTIPLICATION FACTOR!');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│     Room scales by:      cameraRatio only');
      console.log('│     Furniture scales by: (1/scaleFactorX) × cameraRatio = ' + (1/sfx).toFixed(4) + ' × cameraRatio');
      console.log('│');
      console.log('│     ⚡ FURNITURE IS ' + (1/sfx).toFixed(2) + 'x LARGER THAN ROOM SCALING ⚡');
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  WHY INTERNAL FLOW WORKS (but external doesn\'t):');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│  INTERNAL FLOW (upload → edit → save → reload):');
      console.log('│    1. User drags furniture to screen position (500, 300)');
      console.log('│    2. On save: exactPosition = screenPos × scaleFactorX = (500×' + sfx.toFixed(4) + ', 300×' + sfx.toFixed(4) + ') = (' + (500*sfx).toFixed(1) + ', ' + (300*sfx).toFixed(1) + ')');
      console.log('│    3. On reload: screenPos = exactPosition / scaleFactorX = (' + (500*sfx).toFixed(1) + '/' + sfx.toFixed(4) + ') = 500 ✅');
      console.log('│    → ROUND-TRIP WORKS because same transform applied both ways');
      console.log('│');
      console.log('│  EXTERNAL FLOW (CV → API → gallery → editor):');
      console.log('│    1. CV generates exactPosition in world units (e.g., (161, 47) feet)');
      console.log('│    2. API returns exactPosition AS-IS (no × scaleFactorX applied)');
      console.log('│    3. Gallery displays using position (pre-computed screen coords)');
      console.log('│    4. Editor loads and applies: screenPos = exactPosition / scaleFactorX');
      console.log('│       = (161 / ' + sfx.toFixed(4) + ') = ' + (161/sfx).toFixed(1) + ' px');
      console.log('│    → MISMATCH! Editor computes different value than Gallery displayed!');
      console.log('│');
      console.log('└' + '─'.repeat(98) + '┘');
      console.log('');
      
      // =====================================================
      // SECTION 4: WHY SHUFFLE FIXES IT (KEY INSIGHT!)
      // =====================================================
      console.log('┌' + '─'.repeat(98) + '┐');
      console.log('│  🔀 SECTION 4: WHY SHUFFLE FIXES THE SCALE MUTATION                                             │');
      console.log('├' + '─'.repeat(98) + '┤');
      console.log('│');
      console.log('│  USER OBSERVATION: "Once you hit shuffle, everything gets placed properly"');
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  WHAT SHUFFLE DOES:');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│  1. Shuffle triggers an AUTO-SAVE with CURRENT viewport values');
      console.log('│  2. Auto-save updates room.scaleXCanvas from API value to CURRENT viewport scale');
      console.log('│');
      console.log('│     BEFORE shuffle:');
      console.log('│       room.scaleXCanvas = ' + scx.toFixed(6) + ' (from API, for 1920x1080 viewport)');
      console.log('│       current scaleXCanvas = (calculated for current ' + currentWidth + 'x' + currentHeight + ' viewport)');
      console.log('│       cameraRatio = current / room.scaleXCanvas ≠ 1.0');
      console.log('│');
      console.log('│     AFTER shuffle (save updates room.scaleXCanvas):');
      console.log('│       room.scaleXCanvas = current scaleXCanvas (SAME as current!)');
      console.log('│       cameraRatio = current / room.scaleXCanvas = 1.0');
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  THE MATH:');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│     BEFORE shuffle (cameraRatio ≈ ' + (currentWidth / apiDivWidth * 1.45).toFixed(4) + '):');
      console.log('│       Furniture position = rawX / scaleFactorX × 1.0 × cameraRatio');
      console.log('│       = rawX × ' + (1/sfx).toFixed(4) + ' × ' + (currentWidth / apiDivWidth * 1.45).toFixed(4));
      console.log('│       = rawX × ' + ((1/sfx) * (currentWidth / apiDivWidth * 1.45)).toFixed(4) + ' (BLOWN UP!)');
      console.log('│');
      console.log('│     AFTER shuffle (cameraRatio = 1.0):');
      console.log('│       Furniture position = rawX / scaleFactorX × 1.0 × 1.0');
      console.log('│       = rawX × ' + (1/sfx).toFixed(4) + ' (correct scaling!)');
      console.log('│');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│  🎯 THE FIX OPTIONS:');
      console.log('│  ════════════════════════════════════════════════════════════════════════════════════════════');
      console.log('│');
      console.log('│  OPTION A: Sync viewport scale on load (like shuffle does)');
      console.log('│    → Set room.scaleXCanvas = currentScaleXCanvas immediately after loading');
      console.log('│    → This makes cameraRatio = 1.0 from the start');
      console.log('│    → PRO: Simple, matches what shuffle does');
      console.log('│    → CON: May have side effects on room vertex transforms');
      console.log('│');
      console.log('│  OPTION B: Don\'t apply ÷ scaleFactorX for external flow');
      console.log('│    → Detect if exactPosition is already in screen coords');
      console.log('│    → If position.x ≈ exactPosition.x, skip the division');
      console.log('│    → PRO: More surgical fix');
      console.log('│    → CON: Harder to detect, edge cases');
      console.log('│');
      console.log('│  OPTION C: Store coordinate space metadata');
      console.log('│    → Add flag to API response: "coordinateSpace": "screen" or "world"');
      console.log('│    → Transform accordingly');
      console.log('│    → PRO: Clean, explicit');
      console.log('│    → CON: Requires backend change');
      console.log('│');
      console.log('└' + '─'.repeat(98) + '┘');
      console.log('');
      
      // =====================================================
      // SECTION 5: DIAGNOSTIC SUMMARY TABLE
      // =====================================================
      console.log('┌' + '─'.repeat(98) + '┐');
      console.log('│  📊 SECTION 5: DIAGNOSTIC SUMMARY                                                              │');
      console.log('├' + '─'.repeat(98) + '┤');
      console.log('│');
      roomSummaries.forEach((s, idx) => {
        if (idx < 5) {
          console.log('│  ' + (s.item || 'Item').substring(0, 25).padEnd(25) + 
            ' | stored: ' + (s.storedX?.toFixed(0) || 'N/A').padStart(6) + 
            ' | predicted: ' + (s.predictedX?.toFixed(0) || 'N/A').padStart(6) +
            ' | delta: ' + (s.deltaX?.toFixed(0) || 'N/A').padStart(6) +
            ' | blowUp: ' + (s.blowUpFactor?.toFixed(2) || 'N/A').padStart(5) + 'x');
        }
      });
      console.log('│');
      console.log('└' + '─'.repeat(98) + '┘');
      console.log('');
      console.log('▓'.repeat(100));
      console.log('▓▓  END [GALLERY] ULTRA-DEEP DIAGNOSTIC');
      console.log('▓'.repeat(100));
      console.log('');
    }

    const extractedLayouts = roomActiveLayout?.map((index, i) => {
      return roomsData[i]?.layouts?.[index] || {};
    });

    const combinedFurniture = extractedLayouts?.reduce((acc, layout) => {
      if (layout.furniture) {
        acc.push(...layout.furniture);
      }
      return acc;
    }, []);

    return [{ furniture: combinedFurniture || [] }];
  }, [layout, currentIndex]);


   const getHeaders = () => ({
    Authorization: `Bearer ${currentUserSessionStorage}`,
  });

  

  const trackEngagement = React.useCallback((data) => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    const versionId = layout?.layoutsVersions[currentIndex]?.version;
    if (!layout?._id || !versionId) {
      console.warn('TrackEngagement: Missing layoutId or versionId', { layoutId: layout?._id, versionId, currentIndex });
      return;
    }
    postTrackEngagement({
      layoutId: layout?._id,
      versionId: versionId,
      body: data,
    });
  }, [layout?._id, layout?.layoutsVersions, currentIndex, postTrackEngagement]);

  
  // Branded export snapshot source for the gallery. Unlike the editor (which
  // has a live DOM to capture), the gallery reuses the already-saved,
  // brandingless furnished raster (freshest cached blob first, then the
  // full-res furnishedImageId). Returns a data URL, or null on failure so the
  // modal can fall back to the legacy download.
  const getGallerySnapshot = useCallback(async () => {
    try {
      const imageUrl = resolveVersionImageUrl({
        layout,
        index: currentIndex,
        cache: layoutCacheStore,
      });
      if (!imageUrl) return null;
      return await getBase64Image(imageUrl);
    } catch (error) {
      console.warn("[brandedExport] gallery snapshot failed:", error);
      return null;
    }
  }, [layout, currentIndex]);

  const downloadGalleryImage = useCallback(async (setDownloaded) => {
    if (setDownloaded) {
      setDownloaded(true);
    }

    const cachedImage = layoutCacheStore.get(String(layout?._id))?.versions?.[layout?.layoutsVersions[currentIndex]?.version];

    if (layout?.layoutsVersions[currentIndex]?.brandedImage && !cachedImage) {
      const brandedImage = layout?.layoutsVersions[currentIndex]?.brandedImage;
      const fileName = layout?.name + ".jpeg";
      const link = document.createElement("a");
      link.href = await getBase64Image(brandedImage);
      link.download = fileName;
      link.ref = "anonymous"
      link.click();
      return;
    }

    try {
      const imageUrl = resolveVersionImageUrl({
        layout,
        index: currentIndex,
        cache: layoutCacheStore,
      });
      const fileName = layout?.name + ".jpeg";
    
  
      const base64Image = await getBase64Image(imageUrl);
      
        const img = new Image();
        img.src = base64Image;
      
        img.onload = async () => {
          const clone = document.createElement("div");
          clone.style.position = "absolute";
          clone.style.top = "-9999px";
          clone.style.left = "-9999px";
          clone.style.backgroundColor = "white";
          document.body.appendChild(clone);
      
          const imgElement = document.createElement("img");
          imgElement.src = base64Image;
          imgElement.style.objectFit = "contain";
      
          const aspectRatio = 1200 / 789;
          const renderedWidth = Math.min(1200, 789 * aspectRatio);
          imgElement.style.width = `${renderedWidth}px`;
      
          const headerDiv = document.createElement("div");
          headerDiv.style.width = `${renderedWidth}px`;
          headerDiv.style.height = "120px";
          headerDiv.style.display = "flex";
          headerDiv.style.justifyContent = "space-between";
          headerDiv.style.alignItems = "center";
          headerDiv.style.backgroundColor = "#fff";
          headerDiv.style.padding = "0 20px";
          headerDiv.style.boxSizing = "border-box";
      
          const leftDiv = document.createElement("div");
          leftDiv.style.display = "flex";
          leftDiv.style.alignItems = "center";
      
          const logoImage = document.createElement("img");
          logoImage.src =
            (await getBase64Image(userData?.logo)) || "/logo.png";
          logoImage.width = 100;
          logoImage.height = 100;
          logoImage.style.objectFit = "contain";
      
          // Check for tenant-specific logo
          const tenantConfig = getTenantConfig();
          if (userData?.logo?.length == 0 && tenantConfig.logo) {
            logoImage.src = tenantConfig.logo;
          }
      
          if (userData?.logo?.length > 0 || tenantConfig.logo) {
            leftDiv.appendChild(logoImage);
          }
      
          const rightText = document.createElement("div");
          rightText.textContent = layout?.name || "";
          rightText.style.fontSize = `18px`;
          rightText.style.color = "#555";
          rightText.style.textAlign = "right";
          rightText.style.fontFamily = "Montserrat, sans-serif";
      
          if (isUserDataSuccess && userData) {
            headerDiv.appendChild(leftDiv);
            headerDiv.appendChild(rightText);
            clone.appendChild(headerDiv);
          }
      
          clone.appendChild(imgElement);
      
          const footerDiv = document.createElement("div");
          footerDiv.style.width = `${renderedWidth}px`;
          footerDiv.style.minHeight = "fit-content";
          footerDiv.style.display = "flex";
          footerDiv.style.justifyContent = "center";
          footerDiv.style.alignItems = "center";
          footerDiv.style.backgroundColor = "#fff";
          footerDiv.style.fontSize = `18px`;
          footerDiv.style.color = "#888";
          footerDiv.style.padding = "0px 10px";
          footerDiv.style.margin = "20px 0px";
      
          const disclaimerText = document.createElement("div");
          disclaimerText.textContent = userData?.disclaimer || "";
      
          if (isUserDataSuccess && userData) {
            footerDiv.appendChild(disclaimerText);
            clone.appendChild(footerDiv);
          }
      
          // Deterministically decode every raster in the frame, then raster
          // through the shared engine (one path for every download surface).
          await decodeAllImages(clone);

          const dataUrl2 = await rasterizeNode(clone, {
            width: clone.offsetWidth,
            height: clone.offsetHeight,
            as: "png",
          });
      
          const newlink = document.createElement("a");
          newlink.download = fileName;
          newlink.href = dataUrl2;
          newlink.click();
      
          trackEngagement({ downloads: true });

          if (setDownloaded) {
            setDownloaded(false);
          }
        };
    } catch (error) {
      if (setDownloaded) {
        setDownloaded(false);
      }
    }
}, [layout, currentIndex, userData, trackEngagement, isUserDataSuccess]);

  useEffect(() => {
    if(layout && currentIndex !== null && currentIndex !== undefined && layout?.layoutsVersions?.[currentIndex]?.version){
      trackEngagement({ galleryView: true });
    }
    
  }, [layout, currentIndex, trackEngagement]);

  useEffect(() => {
    if (layout && currentIndex !== undefined) {
      startTracking({ layoutData: layout, versionId: layout?.layoutsVersions[currentIndex]?.version });
    }
  }, [layout, currentIndex, startTracking]);

  

  useEffect(() => {
    const inquiryContactItem =
      canConfigureInquiries && layout?._id
        ? [
            {
              icon: <MailOutlineIcon fontSize="small" />,
              label: BUYER_CONTACT_MENU_LABEL,
              value: () => {
                setOptionsMenu(null);
                setUploaderContactModalOpen(true);
              },
            },
          ]
        : [];

    const newMoreMenu = [
      ...inquiryContactItem,
      {
        icon: <ContentCopyOutlinedIcon sx={{ fontSize: 18, color: "#202124" }} />,
        label: "Duplicate Version",
        loadingLabel: "Duplicating",
        loadingDoneLabel: "Editing duplicated version",
        loadingDoneIcon: <CheckOutlinedIcon sx={{ fontSize: 18, color: "#34A853" }} />,
        value: async () => {
          const updationUrl = `${baseUrl}/savedLayout/edit/${layout?._id}?`;
          setDuplicatePerformed(true);
          try {
            const controller = createAbortController();
            await axios.put(
              updationUrl,
              {
                payload: layout?.layoutsVersions[currentIndex]?.payload,
                image: layout?.layoutsVersions[currentIndex]?.imageId,
                furnishedImageId:
                  layout?.layoutsVersions[currentIndex]?.furnishedImage ||
                  layout?.layoutsVersions[currentIndex]?.imageId,
                draft: false,
              },
              {
                signal: controller.signal,
                headers: {
                  Authorization: `Bearer ${currentUserSessionStorage}`,
                }
              }
            );
            eventLog(window.opener, 'floor_plan_event', 'version_duplicated', {
              galleryId: layout?._id,
              galleryUrl: layout?._id ? `${window.location.origin}/floor-gallery/${layout._id}` : undefined,
            });
            setOptionsMenu(null);
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('Failed to duplicate version:', error);
            }
            setOptionsMenu(null);
          }
          refetch();
        },
      },
      {
        icon: layout?.layoutsVersions?.[currentIndex]?.hide ? (
          <VisibilityIcon fontSize="small" />
        ) : (
          <HideGalleryIcon fontSize="small" />
        ),
        label: layout?.layoutsVersions?.[currentIndex]?.hide ? "Show" : "Hide",
        loadingLabel: layout?.layoutsVersions?.[currentIndex]?.hide
          ? "Showing"
          : "Hiding",

        value: async () => {
          const updationUrl = `${baseUrl}/savedLayout/edit/${layout?._id}?version=${layout?.layoutsVersions[currentIndex]?.version}`;

          try {
            const controller = createAbortController();
            await axios.put(
              updationUrl,
              {
                hide: layout?.layoutsVersions[currentIndex]?.hide
                  ? !layout?.layoutsVersions[currentIndex]?.hide
                  : true,
              },
              {
                signal: controller.signal,
                headers: {
                  Authorization: `Bearer ${currentUserSessionStorage}`,
                }
              }
            );
            setOptionsMenu(null);
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('Failed to toggle visibility:', error);
            }
            setOptionsMenu(null);
          }
          refetch();
        },
      },
      {
        icon: <RemoveGalleryIcon fontSize="small" />,
        label: "Remove from Gallery",
        loadingLabel: "Removing",
        labelStyle: { color: "#FF5151", fontSize: "15px", fontWeight: 500 },

        value: async () => {
          try {
            await deleteVersionFromSavedLayout({
              layoutId: layout?._id,
              versionId: layout?.layoutsVersions[currentIndex]?.version,
            }).unwrap();

            setOptionsMenu(null);
          } catch (error) {
            console.error('Failed to remove from gallery:', error);
            setOptionsMenu(null);
          }

          refetch();
        },
      },
    ];
    const updatedMenu =
      userData?._id === layout?.user
        ? newMoreMenu
        : newMoreMenu.filter((menu) => menu.label !== "Remove from Gallery");
    
    setMoreMenu(updatedMenu);
    // REMOVED: The code that was setting optionsMenu here was causing an infinite loop
    // because both shareMenu and moreMenu effects were trying to set optionsMenu,
    // and optionsMenu was in both dependency arrays, causing them to trigger each other.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, layout, userData, deleteVersionFromSavedLayout, refetch, currentUserSessionStorage]);

  useEffect(() => {
    setImageLoading(true);
  }, [currentIndex]);

  useEffect(() => {
    if (duplicatedPerformed && !isFetching) {
      setCurrentIndex(0);
      setDuplicatePerformed(false);
    }
  }, [isFetching, duplicatedPerformed]);

  useEffect(() => {
    if (!layout?.layoutsVersions) return;
    
    const endNumber = layout.layoutsVersions.length - 1;

    let rangeStart = 0;
    let rangeEnd = endNumber + 1;

    const newPaginationRange = [];
    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (params?.id && layout?.layoutsVersions[i]?.hide) continue;
      newPaginationRange.push(i);
    }

    const reversedPaginationRange = [
      ...newPaginationRange.slice(0, -1).reverse(), 
      newPaginationRange[newPaginationRange.length - 1], 
    ];

    setPaginationRange(reversedPaginationRange);
    if (currentIndex == undefined || currentIndex === null) {
      // Skip seeding a default ("newest") index when the editor handed us a
      // specific version to pin via `location.state.editedVersion`. The
      // dedicated effect below owns that path; letting this seeder run too
      // would race against it (sometimes producing a brief "newest" flash
      // before the pin lands, sometimes clobbering the pin entirely on
      // mounts where `layout.layoutsVersions` resolves between the two
      // effects).
      const hasPendingEditedVersionPin =
        location?.state?.editedVersion !== undefined &&
        location?.state?.editedVersion !== null;
      if (!hasPendingEditedVersionPin) {
        mfLogGalleryStale("gallery.pagination.seedIndex", {
          nextIndex: reversedPaginationRange[reversedPaginationRange.length - 2],
          reason: "no-editedVersion-pin",
        });
        setCurrentIndex(
          reversedPaginationRange[reversedPaginationRange.length - 2]
        );
      } else {
        mfLogGalleryStale("gallery.pagination.skipSeed", {
          editedVersion: location?.state?.editedVersion,
        });
      }
    }
  }, [layout?.layoutsVersions, params?.id, currentIndex, location]);

  // One-shot pin for the version the user was just editing.
  //
  // `runBackToGallery` in UploadFloor.js navigates to `/floor-gallery/:id`
  // with `state: { editedVersion }` so the slider opens on that version
  // rather than always falling back to "newest". We honor that signal here,
  // independent of the paginationRange/default-index effect above, so:
  //
  //   - It can't lose to data-fetch races on a fresh mount (where
  //     `layout.layoutsVersions` resolves after a sibling effect has
  //     already seeded `currentIndex`).
  //   - It can't be silently skipped by the `currentIndex == null` gate
  //     after a re-render that didn't unmount the slider but did refresh
  //     `location.state` with a new `editedVersion`.
  //
  // Single-shot: we clear `editedVersion` from history right after
  // honoring it so chip taps and refreshes use the normal default behavior
  // and we don't keep re-pinning on subsequent renders.
  useEffect(() => {
    const editedVersion = location?.state?.editedVersion;
    if (editedVersion === undefined || editedVersion === null) return;
    if (!layout?.layoutsVersions) return;
    const matchedIndex = layout.layoutsVersions.findIndex(
      (v) => v?.version === editedVersion
    );
    mfLogGalleryStale("gallery.pin.editedVersion", {
      editedVersion,
      matchedIndex,
      versionsLen: layout.layoutsVersions.length,
    });
    if (matchedIndex >= 0) setCurrentIndex(matchedIndex);
    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, editedVersion: undefined },
    });
    // We depend on `location` (rather than the narrower
    // `location.state.editedVersion`) so the effect re-runs after the
    // single-shot `navigate(replace)` clears the pin from state. The early
    // returns above guarantee the cleared run is a no-op, so there's no
    // chance of looping. Mirrors the dependency style of the
    // paginationRange effect just above.
  }, [layout?.layoutsVersions, location, navigate]);

  // Guest uploaders: schedule email modal after gallery load. Auth only hides
  // display (open prop below) — canceling the timer while auth is open caused
  // the prompt to never return after signup.
  // Depend on stable layout fields so gallery refetches do not reset the timer.
  useEffect(() => {
    if (!shouldShowGuestContactBanner({ layout, isGuest: isGalleryGuest })) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setUploaderContactModalOpen(true);
    }, 4000);

    return () => window.clearTimeout(timer);
    // layout object identity changes on refetch; only _id / contact email
    // should re-arm the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable layout fields only
  }, [isGalleryGuest, layout?._id, layout?.inquiryContactEmail]);

  const applyVersionStripEdgeFades = useCallback(() => {
    const el = versionChipsScrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const eps = 6;
    if (maxScroll <= eps) {
      setVersionStripEdgeFades({ left: false, right: false });
      return;
    }
    setVersionStripEdgeFades({
      left: scrollLeft > eps,
      right: scrollLeft < maxScroll - eps,
    });
  }, []);

  const handleVersionChipsScroll = useCallback(() => {
    applyVersionStripEdgeFades();
  }, [applyVersionStripEdgeFades]);

  useEffect(() => {
    versionChipProgrammaticScrollRef.current = false;
    setVersionStripScrollable(false);
    setVersionStripEdgeFades({ left: false, right: false });
  }, [layout?._id]);

  useLayoutEffect(() => {
    const el = versionChipsScrollerRef.current;
    if (!el) return;
    const measure = () => {
      const { scrollWidth, clientWidth } = el;
      const eps = 2;
      setVersionStripScrollable(scrollWidth > clientWidth + eps);
      requestAnimationFrame(() => applyVersionStripEdgeFades());
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout?._id, paginationRange, applyVersionStripEdgeFades]);

  useEffect(() => {
    if (currentIndex == null || !paginationRange?.length) return;
    const newVersionSlot = paginationRange[paginationRange.length - 1];
    if (currentIndex === newVersionSlot) return;

    let programmaticScrollTimer;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const chip = activeVersionChipColumnRef.current;
        const scroller = versionChipsScrollerRef.current;
        if (!chip || !scroller) return;
        versionChipProgrammaticScrollRef.current = true;
        chip.scrollIntoView({
          behavior: "smooth",
          inline: "nearest",
          block: "nearest",
        });
        programmaticScrollTimer = window.setTimeout(() => {
          versionChipProgrammaticScrollRef.current = false;
          applyVersionStripEdgeFades();
        }, 480);
      });
    });
    return () => {
      cancelAnimationFrame(id);
      if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer);
    };
  }, [currentIndex, paginationRange, layout?._id, applyVersionStripEdgeFades]);

  useEffect(() => {
    const payload = layout?.layoutsVersions?.[currentIndex]?.payload;
    const roomsData = payload?.roomsData;
    const roomActiveLayout = payload?.roomActiveLayout;

    if (!roomsData || roomsData.length === 0) return;

    const currentRoom = roomsData[selectedTab];
    const currentLayoutId = roomActiveLayout?.[selectedTab];
    const currentFurniture = currentRoom?.layouts?.[currentLayoutId]?.furniture;

    if (!currentFurniture || currentFurniture.length === 0) {
      const nextIndex = roomsData.findIndex((room, index) => {
        const layoutId = roomActiveLayout?.[index];
        const furniture = room?.layouts?.[layoutId]?.furniture;
        return furniture?.length > 0;
      });

      if (nextIndex !== -1) {
        setSelectedTab(nextIndex);
      }
    }
  }, [layout, currentIndex, selectedTab]);

  useEffect(() => {
    if (
      !isFetching &&
      layoutBeingSaved == null &&
      layout?.layoutsVersions[currentIndex]?.version ==
        reloadRequired?.version &&
      layout?._id == reloadRequired?._id
    )
      setReloadRequired(null);
  }, [isFetching, layoutBeingSaved, layout?.layoutsVersions, currentIndex, layout?._id, reloadRequired?.version, reloadRequired?._id]);

  useEffect(() => {    
    let intervalId;
    let retryCount = 0;
    const MAX_RETRIES = 15; // 15 retries × 2s = 30s max before giving up
    const POLL_INTERVAL = 2000; // Poll every 2s instead of 5s for faster image updates
    const shouldRefetch = layout?.layoutsVersions?.[currentIndex]?.hasUnsavedImage;

    if (shouldRefetch) {
      mfLogGalleryStale("gallery.poll.start", {
        version: layout?.layoutsVersions?.[currentIndex]?.version ?? null,
        hasUnsavedImage: true,
        intervalMs: POLL_INTERVAL,
        maxRetries: MAX_RETRIES,
      });
      intervalId = setInterval(() => {
        retryCount++;
        mfLogGalleryStale("gallery.poll.tick", {
          retryCount,
          version: layout?.layoutsVersions?.[currentIndex]?.version ?? null,
        });
        if (retryCount >= MAX_RETRIES) {
          mfLogGalleryStale("gallery.poll.maxRetries", {
            maxRetries: MAX_RETRIES,
          });
          clearInterval(intervalId);
          return;
        }
        refetch();
      }, POLL_INTERVAL);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [layout, currentIndex, refetch]);

  // Cleanup timeouts, abort controllers and track mount status to prevent memory leaks
  useEffect(() => {
    const copiedTimeout = copiedTimeoutRef.current;
    const abortController = abortControllerRef.current;
    
    return () => {
      isMountedRef.current = false;
      if (copiedTimeout) {
        clearTimeout(copiedTimeout);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, []);

  // Helper function to create abort controller for API calls
  const createAbortController = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  };

  if (isError) {
    if (error?.status === 404) setShowVersionLayout(null);
  }

  if (isSuccess && layout?.layoutsVersions?.length == 0) {
    setShowVersionLayout(null);
    navigate("/main");
    refetchDirectoryData();
  }

  const cacheKey = String(showVersionLayout || params?.id);
  const cachedData = layoutCacheStore.get(cacheKey);

  if (isLoading && cachedData?.name == undefined)
    return (
      <Box
        position={"absolute"}
        top={"50%"}
        left={"50%"}
        sx={{
          transform: "translate(-50% , -50%)",
          width: "100%",
          textAlign: "center",
          color: "#3C3C4399",
          letterSpacing: "-0.43px",
          fontSize: "17px",
        }}
      >
        <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
          <IconButton color="inherit">
            <CircularProgress size={19} color="inherit" />
          </IconButton>
          <Typography ml={1} color={"inherit"}>
            Loading
          </Typography>
        </Box>
      </Box>
    );

  // Gallery top-bar geometry. The logo bubble's inset from the top, the left
  // edge and the version strip's inset from the right edge are all the same
  // value, and the strip is exactly as tall as the bubble so the two stay
  // vertically centered on each other.
  // Shared with the floor plan's resting-frame geometry, which derives the
  // clearance it has to leave from these two numbers.
  const galleryTopBarInsetPx = GALLERY_TOP_BAR_INSET_PX;
  const galleryLogoBubbleSizePx = GALLERY_LOGO_BUBBLE_SIZE_PX;
  const galleryLogoGlyphSizePx = 34;
  const galleryLogoBubbleGapPx = 18;
  const galleryTopBarInsetLeft = `calc(env(safe-area-inset-left, 0px) + ${galleryTopBarInsetPx}px)`;
  const galleryTopBarInsetRight = `calc(env(safe-area-inset-right, 0px) + ${galleryTopBarInsetPx}px)`;
  const showNewVersionChip =
    !!currentUserSessionStorage && paginationRange.length >= 1;

  const renderGalleryHeader = (overlay) => {
    const liquidGlassSx = {
      border: `1px solid ${alpha("#FFFFFF", 0.72)}`,
      background: "rgba(248, 248, 250, 0.97)",
      boxShadow: `
                0 -6px 16px ${alpha("#000000", 0.07)},
                0 -2px 8px ${alpha("#0d2847", 0.05)},
                0 10px 16px ${alpha("#0d2847", 0.082)},
                0 2px 12px ${alpha("#000000", 0.038)},
                inset 0 1px 0 ${alpha("#FFFFFF", 0.85)},
                inset 0 -1px 0 ${alpha("#FFFFFF", 0.15)}
              `,
    };

    const logoButton = (
      <Box
        aria-label={overlay ? undefined : "MagicFurnish"}
        role={!overlay && isGalleryUserAuthenticated ? "button" : undefined}
        onClick={
          !overlay && isGalleryUserAuthenticated
            ? handleGalleryLogoClick
            : undefined
        }
        sx={{
          p: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...(!overlay && isGalleryUserAuthenticated ? { cursor: "pointer" } : {}),
          ...(overlay ? { pointerEvents: "auto" } : {}),
        }}
      >
        <LogoIcon
          width={galleryLogoGlyphSizePx}
          height={galleryLogoGlyphSizePx}
        />
      </Box>
    );

    const shareActions = (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        sx={overlay ? { pointerEvents: "auto" } : undefined}
      >
        <Button
          variant="text"
          startIcon={<IosShareIcon fontSize="small" />}
          sx={{
            boxShadow: "none",
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "106.59px",
          }}
          onClick={() => setBrandedExportOpen(true)}
        >
          Share
        </Button>
      </Box>
    );

    return (
      <>
        {overlay ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 0,
              pointerEvents: "none",
              touchAction: "none",
            }}
          >
            <Box
              aria-label={
                overlay && isGalleryUserAuthenticated ? "MagicFurnish" : undefined
              }
              role={
                overlay && isGalleryUserAuthenticated ? "button" : undefined
              }
              onClick={
                overlay && isGalleryUserAuthenticated
                  ? handleGalleryLogoClick
                  : undefined
              }
              sx={{
                position: "absolute",
                top: `${galleryTopBarInsetPx}px`,
                left: galleryTopBarInsetLeft,
                zIndex: 2,
                ...liquidGlassSx,
                width: galleryLogoBubbleSizePx,
                height: galleryLogoBubbleSizePx,
                minWidth: galleryLogoBubbleSizePx,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: isGalleryUserAuthenticated ? "auto" : "none",
                touchAction: "none",
                overflow: "hidden",
                ...(isGalleryUserAuthenticated ? { cursor: "pointer" } : {}),
              }}
            >
              {logoButton}
            </Box>
          </Box>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              gap: 1,
              px: 2,
            }}
          >
            {logoButton}
            {shareActions}
          </Stack>
        )}
        {!overlay && (
          <Divider
            sx={{
              borderColor: "#DADADA",
              my: 1,
              width: "100%",
            }}
          />
        )}
      </>
    );
  };

  return (
    <Box
      className="layout-version-slider-root"
      display={"flex"}
      sx={{
        pt: value === "floor-plan" ? 0 : 2,
        ...(isAdminViewerRoute
          ? {
              flex: 1,
              minHeight: 0,
              height: "100%",
              maxHeight: "100%",
              overflow: "hidden",
            }
          : {
              minHeight: "100vh",
              maxHeight: "100vh",
              "@supports (height: 100dvh)": {
                minHeight: "100dvh",
                maxHeight: "100dvh",
              },
            }),
        width: "100%",
        touchAction: "none",
      }}
    >
      {BRANDED_EXPORT_ENABLED && brandedExportOpen && (
        <BrandedExportModal
          open={brandedExportOpen}
          onClose={() => setBrandedExportOpen(false)}
          getSnapshot={getGallerySnapshot}
          layoutId={layout?._id}
          version={layout?.layoutsVersions?.[currentIndex]?.version}
          isSaved={!!layout?._id}
          fileName={layout?.name || "floor-plan"}
          onLegacyFallback={() => downloadGalleryImage()}
          onDownloaded={() => trackEngagement({ downloads: true })}
          onShared={() => {
            // Mirror the legacy gallery "Copy link" analytics exactly.
            trackEngagement({ share: true });
            eventLog(window.opener, "floor_plan_event", "link_copied", {
              galleryId: layout?._id,
              galleryUrl: layout?._id
                ? `${window.location.origin}/floor-gallery/${layout._id}`
                : undefined,
            });
          }}
        />
      )}
      <Helmet>
        <title>{`${layout?.name || "Untitled"}`}</title>
        <meta name="description" content={`${layout?.name || "Untitled"}`} />
        <meta property="og:title" content={`${layout?.name || "Untitled"}`} />
        <meta
          property="og:description"
          content={`${layout?.name || "Untitled"}`}
        />
        <meta
          property="og:image"
          content={`${
            layout?.layoutsVersions[0]?.furnishedImageId ||
            layout?.layoutsVersions[0]?.image
          }`}
        />
        <meta property="og:url" content={`${window.location.origin}`} />
        <meta name="twitter:card" content={`${layout?.name || "Untitled"}`} />
        <meta name="twitter:title" content={`${layout?.name || "Untitled"}`} />
        <meta
          name="twitter:description"
          content={`${layout?.name || "Untitled"}`}
        />
        <meta
          name="twitter:image"
          content={`${
            layout?.layoutsVersions[0]?.furnishedImageId ||
            layout?.layoutsVersions[0]?.image
          }`}
        />
      </Helmet>

      <Box
        display={"flex"}
        flexDirection={"column"}
        flex={1}
        width={"100%"}
        position={"relative"}
        alignItems={"center"}
        minHeight={0}
        height={isAdminViewerRoute ? "100%" : undefined}
        overflow={isAdminViewerRoute ? "hidden" : undefined}
      >
        {value === "shopping-list" ? renderGalleryHeader(false) : null}

        {value == "floor-plan" ? null : (
          <Container
            sx={{
              maxWidth: "100% !important",
              width: "100%",
              px: "16px !important",
            }}
          >
            <Button
              variant="text"
              startIcon={<ArrowBackIosIcon color="primary" />}
              onClick={() => setValue("floor-plan")}
              sx={{ px: 0, mx: 0, textTransform: "none", whiteSpace: "nowrap" }}
            >
              Back
            </Button>
          </Container>
        )}

        {value == "floor-plan" ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                ...(isAdminViewerRoute
                  ? {
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      maxHeight: "100%",
                    }
                  : {
                      height: "100vh",
                      maxHeight: "100vh",
                      "@supports (height: 100dvh)": {
                        height: "100dvh",
                        maxHeight: "100dvh",
                      },
                    }),
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                position: "relative",
                flexShrink: 0,
              }}
            >
            {(() => {

              const hasUnsavedImage = layout?.layoutsVersions[currentIndex]?.hasUnsavedImage;
              // Only show the polling spinner when there is a *real* cache
              // version mismatch — i.e. we have a cached blob but it was
              // captured for a different version than the one currently being
              // viewed. Without the explicit `!!cachedImage` guard the loose
              // equality compare (`undefined != "<version>"`) used to evaluate
              // to "mismatch" whenever the cache was empty (e.g. first gallery
              // visit, refresh, or a capture that returned a blank blob and
              // was therefore skipped by the size guard in runBackToGallery).
              // That false positive triggered the 5+ s spinner over the
              // previous server image while polling waited for the backend to
              // finish regenerating the new gallery image.
              //
              // With a true cache miss we'd rather fall back to whatever the
              // ProgressiveImage chain serves (the previous server image, or
              // the unfurnished fallback) cleanly. Polling refresh still
              // picks up the regenerated image silently — same end state, no
              // jarring overlay.
              const currentVersion =
                layout?.layoutsVersions[currentIndex]?.version;
              const cacheVersionMismatch =
                !!cachedImage &&
                cachedImage.version !== currentVersion &&
                hasUnsavedImage;
              // If we already have a fresh blob in cachedImage, never show a
              // spinner over it and never hide it behind any gate. The blob
              // IS the ground truth for what was just edited — no loader or
              // gate should obscure it. This is the primary safety net for
              // the requirement "a loader must never hand off to a stale
              // gallery image": if we have the blob we show it immediately,
              // unconditionally.
              const hasFreshBlob = !!cachedImage;
              const shouldShowLoader =
                !hasFreshBlob && (imageLoading || cacheVersionMismatch || postEditFreshGate);

              // Hide the underlying image while the fresh-gate is up so the
              // stale server URL is never painted even for a single frame.
              // Gated on !hasFreshBlob: if we have the fresh blob we always
              // render ProgressiveImage (which will use the blob src), even
              // if postEditFreshGate or imageLoading is still true.
              const shouldHideImage =
                !hasFreshBlob && ((imageLoading && cacheVersionMismatch) || postEditFreshGate);
              return (
                <>
                  {shouldShowLoader && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 10,
                        left: 0,
                        top: 0,
                      }}
                    >
                      <LoaderSpinner text={false} />
                    </Box>
                  )}
                  <Box
                    className={"gallery"}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      bgcolor: "#F5F5F7",
                    }}
                  >
                    <TransformWrapper
                      ref={transformRef}
                      initialScale={1}
                      limitToBounds={false}
                      minScale={0.5}
                      doubleClick={{ disabled: true }}
                      pinch={{ disabled: false }}
                      panning={{ velocityDisabled: true, disabled: false, lockAxisX: false, lockAxisY: false }}
                      wheel={{ smoothStep: 0.005 }}
                      maxScale={5}
                    >
                      <TransformComponent
                        wrapperStyle={{
                          width: "100%",
                          height: "100%",
                          maxHeight: "100%",
                          touchAction: "none",
                        }}
                        contentStyle={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "100%",
                            // Hidden exactly when the loader is over it, so the
                            // two can never both be absent. This used to key off
                            // imageLoading alone, which is set on every
                            // currentIndex change — including the editedVersion
                            // pin on arrival from the editor. With a fresh blob
                            // in hand shouldShowLoader is (correctly) false, so
                            // that combination hid the plan behind nothing at
                            // all: an empty grey frame until the blob decoded.
                            // shouldHideImage is a subset of shouldShowLoader,
                            // so a hidden image is still always covered.
                            display: shouldShowLoader ? "none" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            // Resting-frame only: horizontal breathing room plus
                            // the vertical band the plan is contain-fitted into.
                            // Lives inside the TransformComponent so pinch/wheel
                            // zoom is unaffected — at any scale > 1 the padding
                            // scales away with the content and the user can still
                            // fill / exceed the screen exactly as before.
                            // See src/constants/galleryFit.js for the band.
                            px: { xs: "18px", sm: "36px" },
                            boxSizing: "border-box",
                            ...galleryFitSx,
                          }}
                        >
                          {!shouldHideImage && (
                            <ProgressiveImage
                              key={currentIndex}
                              layout={layout}
                              currentIndex={currentIndex}
                              cachedImage={cachedImage}
                              setImageLoading={setImageLoading}
                              setCachedImage={setCachedImage}
                              layoutCacheStore={layoutCacheStore}
                              imageLoading={imageLoading}
                            />
                          )}
                        </Box>
                      </TransformComponent>
                    </TransformWrapper>
                  </Box>
                </>
              );
            })()}

              {/* Bottom image-fade gradient removed — the persistent branding-strip
                  fade at the viewport bottom is now the single source of truth so
                  the new translucent dark stop (rgba(27,27,27,0.5)) doesn't stack
                  with itself and create a visible mid-gradient seam. */}

              {/* Top fade (#FFFFFF → transparent), same pass-through as bottom gradient */}
              {!optionsMenu && (
                <Box
                  sx={{
                    position: "fixed",
                    top: "env(safe-area-inset-top, 0px)",
                    left: 0,
                    right: 0,
                    height: "120px",
                    zIndex: 1098,
                    pointerEvents: "none",
                    touchAction: "none",
                    background: galleryTopFadeGradient,
                  }}
                />
              )}

              {optionsMenu && (
                <Box
                  maxWidth="sm"
                  sx={{
                    pt: "5px",
                    width: "100%",
                    textAlign: "center",
                    bottom: 0,
                    position: "absolute",
                    zIndex: 13100,
                  }}
                >
                  <LayoutOptionMenu
                    key={currentIndex}
                    refetch={refetch}
                    optionsMenu={optionsMenu}
                    layoutData={layout}
                    onClose={() => setOptionsMenu(null)}
                    paperVariant="bottomSheet"
                  />
                </Box>
              )}

              {!optionsMenu && layout?.layoutsVersions.length > 1 && (
                <Box
                  sx={{
                    position: "fixed",
                    top: `calc(env(safe-area-inset-top, 0px) + ${galleryTopBarInsetPx}px)`,
                    left: 0,
                    right: 0,
                    height: `${galleryLogoBubbleSizePx}px`,
                    pr: galleryTopBarInsetRight,
                    zIndex: 1100,
                    pointerEvents: "none",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: "100%",
                      position: "relative",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        pl: `calc(${galleryTopBarInsetLeft} + ${galleryLogoBubbleSizePx}px + ${galleryLogoBubbleGapPx}px)`,
                        pr: showNewVersionChip ? "6px" : 0,
                        boxSizing: "border-box",
                        justifyContent: "flex-end",
                      }}
                    >
                    {paginationRange?.length >= 1 && (
                      <Box
                        sx={{
                          width: "max-content",
                          maxWidth: "100%",
                          minWidth: 0,
                          flexShrink: 1,
                          height: "32px",
                          borderRadius: "980px",
                          background: "rgba(118, 118, 128, 0.22)",
                          border: "0.5px solid rgba(0, 0, 0, 0.06)",
                          boxSizing: "border-box",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          ref={versionChipsScrollerRef}
                          onScroll={handleVersionChipsScroll}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            width: "100%",
                            maxWidth: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            overflowX: "auto",
                            overflowY: "hidden",
                            touchAction: "pan-x",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehaviorX: "contain",
                            pointerEvents: "auto",
                            position: "relative",
                            zIndex: 1,
                            "::-webkit-scrollbar": { display: "none" },
                            msOverflowStyle: "none",
                            scrollbarWidth: "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: "2px",
                              padding: "2px",
                              flexShrink: 0,
                              boxSizing: "border-box",
                              position: "relative",
                              isolation: "isolate",
                            }}
                          >
                            {paginationRange.length > 3 &&
                              versionStripScrollable && (
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: "sticky",
                                    left: 0,
                                    flex: "0 0 auto",
                                    width: `${chipStripEdgeFadeWidthPx}px`,
                                    alignSelf: "stretch",
                                    minHeight: 28,
                                    zIndex: 2,
                                    pointerEvents: "none",
                                    opacity: versionStripEdgeFades.left ? 1 : 0,
                                    transition: "opacity 180ms ease",
                                    background: `linear-gradient(to right, ${alpha(
                                      "#EBEBF0",
                                      0.96
                                    )} 0%, ${alpha("#EBEBF0", 0)} 100%)`,
                                  }}
                                />
                              )}
                          {paginationRange?.map((pageNum, index) => {
                            if (index === paginationRange.length - 1) return null;
                            const isActive = currentIndex === pageNum;
                            return (
                              <Box
                                key={pageNum}
                                ref={isActive ? activeVersionChipColumnRef : null}
                                sx={{
                                  position: "relative",
                                  zIndex: isActive ? 3 : 1,
                                  padding: "0 10px",
                                  height: "28px",
                                  borderRadius: "980px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  boxSizing: "border-box",
                                  flexShrink: 0,
                                  cursor: "pointer",
                                  background: isActive
                                    ? "rgba(255, 255, 255, 0.99)"
                                    : "transparent",
                                  boxShadow: isActive
                                    ? "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)"
                                    : "none",
                                  color: isActive
                                    ? galleryChipBlack
                                    : galleryChipMuted,
                                  fontWeight: isActive ? 600 : 400,
                                  transition:
                                    "background 200ms ease, box-shadow 200ms ease, color 120ms ease, font-weight 120ms ease",
                                  "@media (hover: hover) and (pointer: fine)": {
                                    "&:hover": {
                                      background: isActive
                                        ? "rgba(255, 255, 255, 0.99)"
                                        : "transparent",
                                      color: isActive
                                        ? galleryChipBlack
                                        : galleryChipBlack,
                                      fontWeight: 600,
                                    },
                                  },
                                }}
                                onClick={() => setCurrentIndex(pageNum)}
                              >
                                <Box
                                  sx={{
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                    fontWeight: "inherit",
                                    color: "inherit",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {index === 0
                                    ? "Unfurnished"
                                    : paginationRange.length === 3
                                    ? "Furnished"
                                    : "Layout " + index}
                                </Box>
                                {layout?.layoutsVersions?.[pageNum]?.hide && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: "50%",
                                      top: "50%",
                                      transform: "translate(-40% , -50%)",
                                    }}
                                  >
                                    <UneyeIcon />
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                            {paginationRange.length > 3 &&
                              versionStripScrollable && (
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: "sticky",
                                    right: 0,
                                    flex: "0 0 auto",
                                    width: `${chipStripEdgeFadeWidthPx}px`,
                                    alignSelf: "stretch",
                                    minHeight: 28,
                                    zIndex: 2,
                                    pointerEvents: "none",
                                    opacity: versionStripEdgeFades.right ? 1 : 0,
                                    transition: "opacity 180ms ease",
                                    background: `linear-gradient(to left, ${alpha(
                                      "#EBEBF0",
                                      0.96
                                    )} 0%, ${alpha("#EBEBF0", 0)} 100%)`,
                                  }}
                                />
                              )}
                        </Box>
                      </Box>
                      </Box>
                    )}
                    </Box>
                  </Box>
                  {showNewVersionChip && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                        pl: "4px",
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 2,
                        boxSizing: "border-box",
                        backgroundColor: "transparent",
                      }}
                    >
                      {(() => {
                        // The "+New" chip silently no-ops for logged-in users
                        // when there's no source layout to fork from (a brief
                        // window after mount before `currentIndex` is set, or
                        // if `layoutsVersions[currentIndex]` is missing).
                        // Surface that as a visually disabled state instead of
                        // making the tap feel broken. Guests are unaffected —
                        // their tap routes to /signup with a redirectPath.
                        const newChipDisabled =
                          !!currentUserSessionStorage &&
                          (currentIndex == null ||
                            !layout?.layoutsVersions?.[currentIndex]);
                        return (
                        <Box
                          key={paginationRange[paginationRange.length - 1]}
                          sx={{
                            position: "relative",
                            padding: "3px 10px",
                            minWidth: "fit-content",
                            height: "32px",
                            borderRadius: "980px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            boxSizing: "border-box",
                            ...galleryChipGlassSx,
                            ...(currentIndex ===
                            paginationRange[paginationRange.length - 1]
                              ? galleryChipGlassActiveSx
                              : galleryNewVersionChipIdleSx),
                            cursor: creatingNewVersion || newChipDisabled
                              ? "not-allowed"
                              : "pointer",
                            opacity: newChipDisabled ? 0.45 : 1,
                            fontWeight:
                              currentIndex ===
                              paginationRange[paginationRange.length - 1]
                                ? 600
                                : 400,
                            transition:
                              "background-color 120ms ease, border-color 120ms ease, opacity 120ms ease",
                            "@media (hover: hover) and (pointer: fine)": {
                              "&:hover": creatingNewVersion || newChipDisabled
                                ? undefined
                                : currentIndex ===
                                    paginationRange[
                                      paginationRange.length - 1
                                    ]
                                  ? {
                                      backgroundColor: alpha(
                                        "#FFFFFF",
                                        0.96
                                      ),
                                      borderColor: alpha("#000000", 0.14),
                                    }
                                  : {
                                      ...galleryChipGlassHoverSx,
                                      background:
                                        "rgba(255, 255, 255, 0.98)",
                                    },
                            },
                          }}
                          onClick={async (e) => {
                            if (creatingNewVersion || newChipDisabled) return;
                            if (!currentUserSessionStorage) {
                              // =====================================================
                              // PR #93: VERSION CREATE CLICK (NOT LOGGED IN)
                              // =====================================================
                              const galleryPath = `/floor-gallery/${layout?.uuid}`;

                              console.log('');
                              console.log('╔' + '═'.repeat(60) + '╗');
                              console.log('║  ➕ [VERSION CREATE] Button Clicked (User NOT logged in)   ║');
                              console.log('╠' + '═'.repeat(60) + '╣');
                              console.log('║  layoutUuid:', layout?.uuid);
                              console.log('║  galleryPath:', galleryPath);
                              console.log('║  ');
                              console.log('║  📝 Setting sessionStorage.redirectPath =', galleryPath);
                              console.log('║  🚀 Navigating to /signup...');
                              console.log('╚' + '═'.repeat(60) + '╝');
                              console.log('');

                              // Store redirect path so user returns to gallery after login
                              sessionStorage.setItem(
                                "redirectPath",
                                galleryPath
                              );
                              navigate("/signup");
                              return;
                            }

                            e.preventDefault();
                            e.stopPropagation();

                            const updationUrl = `${baseUrl}/savedLayout/edit/${layout?._id}?`;
                            const sourceLayout =
                              currentIndex != null
                                ? layout?.layoutsVersions?.[currentIndex]
                                : null;

                            if (!sourceLayout) return;

                            setCreatingNewVersion(true);
                            try {
                              let payloadForNew =
                                sourceLayout.payload ?? null;
                              let imageId = sourceLayout.imageId;
                              let furnishedImageId =
                                sourceLayout.furnishedImage ||
                                sourceLayout.imageId ||
                                null;

                              // Unfurnished / version rows often omit `payload` in the list response;
                              // fetch full layout so "New" can fork an editable version of the blank plan.
                              if (
                                !payloadForNew &&
                                sourceLayout.version != null
                              ) {
                                const layoutIdentifier =
                                  layout?.uuid || layout?._id;
                                if (layoutIdentifier) {
                                  try {
                                    const detailRes = await axios.get(
                                      `${baseUrl}/savedLayout/getByIdentifier/${layoutIdentifier}?version=${sourceLayout.version}`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${currentUserSessionStorage}`,
                                        },
                                      }
                                    );
                                    const fullLayout =
                                      detailRes?.data?.data ?? detailRes?.data;
                                    payloadForNew =
                                      fullLayout?.payload ?? null;
                                    imageId =
                                      imageId ??
                                      fullLayout?.imageId ??
                                      fullLayout?.image;
                                    furnishedImageId =
                                      furnishedImageId ||
                                      fullLayout?.furnishedImageId ||
                                      fullLayout?.furnishedImage ||
                                      imageId ||
                                      null;
                                  } catch (err) {
                                    console.error(
                                      "Error loading layout for new version:",
                                      err
                                    );
                                  }
                                }
                              }

                              if (!payloadForNew) return;

                              const controller = createAbortController();
                              const response = await axios.put(
                                updationUrl,
                                {
                                  payload: payloadForNew,
                                  image: imageId,
                                  furnishedImageId:
                                    furnishedImageId || imageId || null,
                                  draft: false,
                                },
                                {
                                  signal: controller.signal,
                                  headers: {
                                    Authorization: `Bearer ${currentUserSessionStorage}`,
                                  },
                                }
                              );

                              eventLog(window.opener, 'floor_plan_event', 'version_duplicated', {
                                galleryId: layout?._id,
                                galleryUrl: layout?._id ? `${window.location.origin}/floor-gallery/${layout._id}` : undefined,
                              });
                              setShowVersionLayout(null);
                              setEditVersionLayout(response?.data?.layout?.version);
                              setActiveLayout(layout);
                              navigate(
                                `/floor-plan/${layout?.uuid}/version/${response?.data?.layout?.version}`
                              );
                              refetch();
                            } catch (error) {
                              if (error.name !== 'AbortError') {
                                console.error("Error updating layout:", error);
                              }
                            } finally {
                              setCreatingNewVersion(false);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              color: galleryChipBlack,
                              opacity: 1,
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: "inherit",
                                fontSize: "11px",
                                whiteSpace: "nowrap",
                                color: galleryChipBlack,
                                opacity: 1,
                              }}
                            >
                              {creatingNewVersion ? (
                                <CircularProgress
                                  size={14}
                                  sx={{ color: galleryChipBlack }}
                                />
                              ) : (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    color: galleryChipBlack,
                                    opacity: 1,
                                  }}
                                >
                                  <PlusIcon
                                    color="currentColor"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      flexShrink: 0,
                                      opacity: 1,
                                    }}
                                  />
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "11px",
                                      color: galleryChipBlack,
                                      opacity: 1,
                                      letterSpacing: "-0.01em",
                                    }}
                                  >
                                    New
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            {layout?.layoutsVersions?.[
                              paginationRange[paginationRange.length - 1]
                            ]?.hide && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: "50%",
                                  top: "50%",
                                  transform: "translate(-40% , -50%)",
                                }}
                              >
                                <UneyeIcon />
                              </Box>
                            )}
                          </Box>
                        </Box>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              )}

              {!optionsMenu && (
                <Box
                  sx={{
                    position: "fixed",
                    bottom: "calc(max(16px, env(safe-area-inset-bottom, 16px)) + 26px)",
                    left: 0,
                    right: 0,
                    zIndex: 12050,
                    pointerEvents: "none",
                    display: "flex",
                    justifyContent: "center",
                    px: 2,
                  }}
                >
                  {/* Narrow screens stack "Shuffle it" above the Share /
                      I'm Interested row. Once there's room for all three
                      pills they share one row, ordered Shuffle it, Share,
                      then I'm Interested. */}
                  <Box
                    sx={{
                      pointerEvents: "auto",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                      flexWrap: "nowrap",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      maxWidth: { xs: "min(100%, 420px)", sm: "min(100%, 720px)" },
                    }}
                  >
                    {layout?.layoutsVersions.length > 1 &&
                      layout?.layoutsVersions[currentIndex]?.version != 1 && (
                        <GlassActionButton
                          icon={<ShuffleIcon />}
                          onClick={async () => {
                            if (!currentUserSessionStorage) {
                              // =====================================================
                              // GUEST EDITOR: Enter editor directly without auth
                              // =====================================================
                              // No server-side fork yet — guests browse and edit
                              // in-memory until they trigger Save/Share/Download,
                              // which fires the auth wall. The actual /savedLayout/copy
                              // is made inside saveForkedLayoutAndNavigate after auth
                              // succeeds, gated by an ownership check so an owner
                              // logging back in does NOT fork their own plan.
                              // =====================================================
                              console.log(
                                "[CUSTOMIZE] Guest entering editor directly (no auth wall)"
                              );

                              const targetVersion =
                                layout?.layoutsVersions[currentIndex]?.version;
                              setEditVersionLayout(targetVersion);
                              setActiveLayout(layout);

                              // Open the editor already in room-pick mode, so
                              // "Shuffle it" lands on the same state as tapping
                              // Shuffle inside the editor. Keyed by the uuid the
                              // editor will resolve from activeLayout.
                              setPendingShuffleIntent(layout?.uuid);

                              // Navigate to floor-gallery route (allowed for guests)
                              navigate(`/floor-gallery/${layout?._id}`, {
                                replace: true,
                              });
                            } else {
                              // =====================================================
                              // PR #93: CUSTOMIZE CLICK (USER LOGGED IN)
                              // =====================================================
                              const editorPath = `/floor-plan/${layout?.uuid}/version/${layout?.layoutsVersions[currentIndex]?.version}`;
                              const isOtherUsersLayout =
                                layout?.user !== userData?._id && !clientRef;

                              console.log("");
                              console.log("╔" + "═".repeat(60) + "╗");
                              console.log(
                                "║  🎨 [CUSTOMIZE] Button Clicked (User IS logged in)         ║"
                              );
                              console.log("╠" + "═".repeat(60) + "╣");
                              console.log("║  layoutUuid:", layout?.uuid);
                              console.log(
                                "║  version:",
                                layout?.layoutsVersions[currentIndex]?.version
                              );
                              console.log("║  editorPath:", editorPath);
                              console.log("║  isOtherUsersLayout:", isOtherUsersLayout);
                              console.log("║  willCopyFirst:", isOtherUsersLayout);
                              console.log("║  ");
                              console.log(
                                "║  🚀 Direct navigation to editor (no auth redirect needed)"
                              );
                              console.log("╚" + "═".repeat(60) + "╝");
                              console.log("");

                              let response;

                              if (
                                isOtherUsersLayout &&
                                layout?._id === originalLayoutId
                              ) {
                                _console.log(
                                  "[CUSTOMIZE] Copying layout first (not owned by user)..."
                                );
                                try {
                                  const pathCopy = `/savedLayout/copy/${layout?._id}`;

                                  response = await axios.post(
                                    `${baseUrl}${pathCopy}`,
                                    { parent: null },
                                    { headers: getHeaders() }
                                  );
                                  console.log(
                                    "[CUSTOMIZE] ✅ Layout copied, new UUID:",
                                    response?.data?.data?.uuid
                                  );
                                } catch (error) {
                                  console.log(
                                    "[CUSTOMIZE] ❌ Copy failed:",
                                    error?.message
                                  );
                                }
                              }

                              // =====================================================
                              // FIX: NAVIGATE FIRST, THEN SET STATE
                              // =====================================================
                              const targetUuid = response
                                ? response?.data?.data?.uuid
                                : layout?.uuid;
                              const targetVersion =
                                layout?.layoutsVersions[currentIndex]?.version;
                              const editorUrl = `/floor-plan/${targetUuid}/version/${targetVersion}`;

                              console.log("[CUSTOMIZE] 🚀 NAVIGATING TO EDITOR:");
                              console.log("  targetUuid:", targetUuid);
                              console.log("  targetVersion:", targetVersion);
                              console.log("  editorUrl:", editorUrl);
                              console.log("  params?.id:", params?.id);

                              setEditVersionLayout(targetVersion);
                              setActiveLayout(
                                response ? response?.data?.data : layout
                              );

                              // Open the editor already in room-pick mode, so
                              // "Shuffle it" lands on the same state as tapping
                              // Shuffle inside the editor. Keyed by targetUuid
                              // so a copied layout arms its own editor, not the
                              // original's.
                              setPendingShuffleIntent(targetUuid);

                              navigate(editorUrl, { replace: true });

                              console.log("[CUSTOMIZE] ✅ Navigation initiated");
                            }
                          }}
                        >
                          Shuffle it
                        </GlassActionButton>
                      )}
                    {showInquireControl && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <GlassActionButton
                          icon={<IosShareIcon />}
                          onClick={() => setBrandedExportOpen(true)}
                        >
                          Share
                        </GlassActionButton>
                        <GlassActionButton
                          variant="solid"
                          disableRipple={inquiryAlreadySent}
                          tabIndex={inquiryAlreadySent ? -1 : 0}
                          aria-disabled={
                            inquiryAlreadySent || inquireSubmitting || undefined
                          }
                          icon={
                            inquiryAlreadySent ? (
                              <CheckOutlinedIcon />
                            ) : (
                              <FavoriteBorderIcon />
                            )
                          }
                          sx={inquiryAlreadySent && galleryInquireSentCtaSx}
                          onClick={
                            inquiryAlreadySent || inquireSubmitting
                              ? undefined
                              : handleInterestTap
                          }
                        >
                          {inquiryAlreadySent
                            ? "Interest sent"
                            : inquireSubmitting
                            ? "Sending…"
                            : "I'm Interested"}
                        </GlassActionButton>
                      </Box>
                    )}
                    {!showInquireControl && (
                      <GlassActionButton
                        variant="solid"
                        icon={<IosShareIcon />}
                        sx={{ minWidth: 108 }}
                        onClick={() => setBrandedExportOpen(true)}
                      >
                        Share
                      </GlassActionButton>
                    )}
                  </Box>
                </Box>
              )}

            </Box>
            {/* MF logo above chips, gradients, and floating CTAs; still below MUI Modal (1300) if needed */}
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 13050,
                pointerEvents: "none",
                touchAction: "none",
                pt: "env(safe-area-inset-top, 0px)",
              }}
            >
              {renderGalleryHeader(true)}
            </Box>
          </>
        ) : (
          <>
            <Container
              maxWidth={"sm"}
              sx={{
                mb: "10px",
                overflow: "auto",
                touchAction: "pan-x pan-y",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
              }}
              ref={containerRef}
            >
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
              }}>
                <Box width="33%">
                  <Typography fontSize={"16px"} fontWeight={600}>
                    {/* Shopping List */}
                  </Typography>
                </Box>
                <Box width="33%" display="flex" justifyContent="center">
                </Box>
                <Box width="33%" display="flex" justifyContent="flex-end" sx={{cursor: "pointer"}} onClick={()=> {
                  setViewAll(true)
                  setSelectedTab(-1)
                  }}>
                  <Typography fontSize={"16px"} fontWeight={400}>View All</Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: "8px",
                  overflowX: "auto",
                  touchAction: "pan-x",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {layout?.layoutsVersions?.[currentIndex]?.payload?.roomsData
                  ?.length > 0 &&
                  layout?.layoutsVersions?.[
                    currentIndex
                  ]?.payload?.roomsData?.map((room, index) => {
                    const hasFurniture =
                      room.layouts?.[
                        layout?.layoutsVersions?.[currentIndex]?.payload
                          ?.roomActiveLayout?.[index]
                      ]?.furniture?.length > 0;
                    if (!hasFurniture) {
                      return null;
                    }

                    return (
                      <Button
                        key={index}
                        onClick={() => {
                          setSelectedTab(index)
                          setViewAll(false)
                        }}
                        sx={{
                          minWidth: "fit-content",
                          height: "40px",
                          padding: "12px 12px",
                          borderRadius: "8px !important",
                          backgroundColor:
                            (index === selectedTab && !viewAll) ? "#2021244D" : "#FFFFFF",
                          boxShadow: "0px 2px 10px 0px #00000040",
                          color: (index === selectedTab && !viewAll) ? "white" : "#20212480",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            backgroundColor:
                              (index === selectedTab && !viewAll) ? "#2021244D" : "#FFFFFF",
                          },
                        }}
                      >
                        {getRoomLabel(room?.zones || "")}
                      </Button>
                    );
                  })}
              </Box>


              {partnerPackage?.productVisibility ? 
              <PartnerShoppingList
                cartItems={cartItems}
                setCartItems={setCartItems}
                containerRef={containerRef}
                items={
                  viewAll ? finalLayout : (
                    layout?.layoutsVersions?.[currentIndex]?.payload?.roomsData?.[
                      selectedTab
                    ]?.layouts || []
                  )
                }
                index={
                  viewAll ? 0 : (
                    layout?.layoutsVersions?.[currentIndex]?.payload
                      ?.roomActiveLayout?.[selectedTab]
                  )
                }
              /> : 
              <ShoppingList
                cartItems={cartItems}
                setCartItems={setCartItems}
                containerRef={containerRef}
                items={
                  viewAll ? finalLayout : (
                    layout?.layoutsVersions?.[currentIndex]?.payload?.roomsData?.[
                      selectedTab
                    ]?.layouts || []
                  )
                }
                index={
                  viewAll ? 0 : (
                    layout?.layoutsVersions?.[currentIndex]?.payload
                      ?.roomActiveLayout?.[selectedTab]
                  )
                }
              />}

            </Container>
          </>
        )}
      </Box>

      {/* ── Persistent "Powered by MagicFurnish" footer + fade gradient ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10002,
          pointerEvents: "none",
          touchAction: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            // Single tall gradient replaces the former 28px fade + flat strip.
            // pointerEvents/touchAction remain "none" on the parent so all
            // gestures still pass through to the floor plan unchanged.
            // Gradient string lives in src/theme/spatial/tokens/gradients.js
            // (extracted verbatim).
            background: editorBottomFadeGradient,
            pt: { xs: "min(360px, 56vh)", sm: "min(280px, 46vh)" },
            pb: "max(8px, env(safe-area-inset-bottom, 0px))",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 400,
              color: "rgba(200,200,200,0.85)",
              lineHeight: 1.3,
            }}
          >
            Powered by MagicFurnish
          </Typography>
        </Box>
      </Box>

      <GalleryInterestToast
        open={interestToastOpen && !inquireModalOpen}
        email={inquireConfirmState?.email}
        error={inquireError}
        onClose={handleCloseInterestToast}
        onRetry={handleRetryInterest}
        bottom={galleryInterestToastBottom}
      />

      <GalleryInquireModal
        open={inquireModalOpen}
        onClose={handleCloseInquireModal}
        isGuest={isGalleryGuest}
        accountEmail={userData?.email}
        onSubmit={submitGalleryInquiry}
        submitting={inquireSubmitting}
        confirmState={inquireConfirmState}
        error={inquireError}
        inquiriesAvailable={layoutInquiriesAvailable}
        guestEmailPrefill={inquireGuestEmailPrefill}
        knownEmail={inquireKnownEmail}
      />

      <GalleryUploaderContactModal
        open={shouldDisplayUploaderContactModal({
          scheduledOpen: uploaderContactModalOpen,
          authModalOpen,
        })}
        onClose={handleCloseUploaderContactModal}
        accountEmail={userData?.email}
        savedContactEmail={layout?.inquiryContactEmail}
        savedInquiryDisplayName={layout?.inquiryDisplayName}
        onSubmit={handleSaveUploaderContact}
        submitting={uploaderContactSubmitting}
        saved={uploaderContactSaved}
        error={uploaderContactError}
      />

      <AuthModal
        open={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
          guestAuthPrefillEmailRef.current = "";
          setAuthPrefillEmail("");
        }}
        onSuccess={handleGalleryAuthSuccess}
        initialEmail={authPrefillEmail}
      />
    </Box>
  );
}

const ProgressiveImage = ({
  layout,
  currentIndex,
  cachedImage,
  setImageLoading,
  setCachedImage,
  layoutCacheStore,
}) => {
  const [currentImgSrc, setCurrentImgSrc] = useState(null);
  // Progressive image loading state - currently disabled
  // eslint-disable-next-line no-unused-vars
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showBlur, setShowBlur] = useState(true);
  const galleryLogRef = useRef(null);

  const lowResSrc =
    layout?.layoutsVersions?.[currentIndex]?.quality50FurnishedImage ||
    layout?.layoutsVersions?.[currentIndex]?.quality50Image;

  const highResSrc =
    layout?.layoutsVersions?.[currentIndex]?.furnishedImageId ||
    layout?.layoutsVersions?.[currentIndex]?.image;

  useEffect(() => {
    setCurrentImgSrc(lowResSrc || highResSrc || null);
    setIsHighResLoaded(false);
    setShowBlur(!!lowResSrc && !!highResSrc);
  }, [lowResSrc, highResSrc, currentIndex]);

  useEffect(() => {
    if (lowResSrc && highResSrc && lowResSrc !== highResSrc) {
      const img = new window.Image();
      img.src = highResSrc;
      img.onload = () => {
        setCurrentImgSrc(highResSrc);
        setIsHighResLoaded(true);
        setShowBlur(false);
      };
    } else {
      setIsHighResLoaded(true);
      setShowBlur(false);
    }
  }, [lowResSrc, highResSrc, currentIndex]);

  useEffect(() => {
    const paint = cachedImage?.url
      ? {
          mode: "blob-cache",
          effective: summarizeImageUrl(cachedImage.url),
          version: cachedImage.version,
        }
      : {
          mode: "progressive",
          lowRes: summarizeImageUrl(lowResSrc),
          highRes: summarizeImageUrl(highResSrc),
          effective: summarizeImageUrl(
            (cachedImage?.url ? cachedImage.url : null) ||
              (lowResSrc || highResSrc || null)
          ),
        };
    mfLogGalleryStale("gallery.progressiveImage.src", {
      currentIndex,
      ...paint,
    });
  }, [lowResSrc, highResSrc, cachedImage, currentIndex, currentImgSrc]);

  let imgSrc = currentImgSrc;
  let imgAlt = `Layout version`;
  let imgHandlers = {};

  if (cachedImage && cachedImage.url) {
    imgSrc = cachedImage.url;
    imgHandlers = {
      onLoad: (e) => {
        mfLogGalleryStale("gallery.progressiveImage.onload", {
          currentIndex,
          mode: "blob-cache",
          loaded: summarizeImageUrl(
            e?.currentTarget?.currentSrc || e?.currentTarget?.src
          ),
        });
        setImageLoading(false);
      },
      onError: (e) => {
        mfLogGalleryStale("gallery.progressiveImage.onerror", {
          currentIndex,
          mode: "blob-cache",
          attempted: summarizeImageUrl(cachedImage?.url),
        });
        setImageLoading(false);
        setCachedImage(null);
        const oldCache = layoutCacheStore.get(String(layout?._id));
        oldCache.versions[layout?.layoutsVersions[currentIndex]?.version] = null;
        layoutCacheStore.set(String(layout?._id), oldCache);
      },
    };
  } else {
    imgHandlers = {
      onLoad: (e) => {
        mfLogGalleryStale("gallery.progressiveImage.onload", {
          currentIndex,
          mode: "progressive-cdn",
          loaded: summarizeImageUrl(
            e?.currentTarget?.currentSrc || e?.currentTarget?.src
          ),
        });
        setImageLoading(false);
      },
      onError: (e) => {
        mfLogGalleryStale("gallery.progressiveImage.onerror", {
          currentIndex,
          mode: "progressive-cdn",
          attempted: summarizeImageUrl(
            e?.currentTarget?.currentSrc || e?.currentTarget?.src
          ),
        });
        setImageLoading(false);
      },
    };
  }

  // [BEDROCK-GALLERY] Log what the gallery is showing vs what the editor will show
  // GUARD: Only log once per layout change (was firing 30+ times per render, crashing console)
  const _galleryLogKey = layout?.layoutsVersions?.[currentIndex]?.payload?.roomsData?.[0]?.layouts?.[0]?.furniture?.[0]?.name + '_' + currentIndex;
  if (process.env.NODE_ENV !== 'production' && layout?.layoutsVersions?.[currentIndex]?.payload && _galleryLogKey !== galleryLogRef.current) {
    galleryLogRef.current = _galleryLogKey;
    const _p = layout.layoutsVersions[currentIndex].payload;
    const _room0 = _p.roomsData?.[0];
    const _furn0 = _room0?.layouts?.[_p.roomActiveLayout?.[0] || 0]?.furniture?.[0];
    if (_furn0) {
      console.log('[BEDROCK-GALLERY] Layout:', currentIndex, '| Item:', _furn0.name, '| w:', _furn0.width, '| origW:', _furn0.origionalWidth, '| room.scaleX:', _room0?.scaleX, '| scaleXCanvas:', _room0?.scaleXCanvas);
    }
  }

  if (!imgSrc) {
    return (
      <Box
        key={currentIndex}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} sx={{ color: "rgba(60, 60, 67, 0.3)" }} />
      </Box>
    );
  }

  return (
    <img
      key={currentIndex}
      data-ph-capture="true"
      src={imgSrc}
      alt={imgAlt}
      style={{
        width: "auto",
        height: "auto",
        maxWidth: "100%",
        maxHeight: "100%",
        display: "block",
        transition: "filter 0.4s cubic-bezier(.4,0,.2,1)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.28), 0 0 1px rgba(0,0,0,0.2)",
        borderRadius: "2px",
      }}
      {...imgHandlers}
    />
  );
};

export default LayoutVersionSlider;
